import { ChildPassengersForm } from "@tripbox/ui";
import { PortalActivityAgeRates, type PortalAgeSelection } from "../components/PortalActivityAgeRates";
import type { ChildPassenger } from "../../../web/src/lib/childPricing";
import type { DownloadItineraryOptions } from "@/components";
import { DownloadItineraryModal } from "@/components";
import { FileDownload } from "@/components/FileDownload";
import type { FormInstance, FormListOperation } from "antd";
import {
  Alert,
  Anchor,
  App as AntApp,
  Button,
  DatePicker,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Skeleton,
  Tag,
  TimePicker,
} from "antd";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BedDoubleIcon,
  BusIcon,
  CalendarDaysIcon,
  Clock3Icon,
  FileTextIcon,
  MapPinIcon,
  MessageSquareMoreIcon,
  LayoutGridIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  RouteIcon,
  SendIcon,
  TentTreeIcon,
  Trash2Icon,
  UsersRoundIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useBeforeUnload,
  useBlocker,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router";
import {
  PortalInventoryItem,
  PortalInventoryType,
  PortalItineraryPayload,
  PortalTemplatePlan,
  downloadPortalItinerary,
  resolvePortalInventoryItems,
  usePortalCountriesQuery,
  usePortalInventoryQuery,
  usePortalItineraryDefaultsQuery,
  usePortalItineraryDraftQuery,
  usePortalLinkedTransportsQuery,
  usePortalQuoteMarkupQuery,
  usePortalTotalPriceMutation,
  usePortalTripRequestQuery,
  useDuplicatePortalItineraryMutation,
  useSavePortalItineraryDraftMutation,
  useSavePortalQuoteMarkupMutation,
  useSubmitPortalItineraryMutation,
} from "../actions/itinerary.actions";
import { QuoteMarkupModal } from "../components/QuoteMarkupModal";
import { QuoteActionsCard, QuotePricingCard } from "../components/QuotePricingCard";
import { useMediaQuery } from "../hooks/useMediaQuery";
import PortalInventoryBrowserModal from "../components/PortalInventoryBrowserModal";
import { PortalPageHeader, PortalPageShell } from "../components/PortalProduct";
import PortalRoutePlannerPage, { PortalRouteStop } from "../components/PortalRoutePlannerPage";
import PortalTemplatePickerPage from "../components/PortalTemplatePickerPage";
import type { PortalWorkspaceContext } from "../components/PortalWorkspaceLayout";
import { matchesInventorySearch } from "../lib/inventorySearch";
import { draftToMarkup, emptyMarkupDraft, markupToDraft, type MarkupDraft } from "../lib/quoteMarkup";
import { getPortalPaths } from "../lib/routes";
import { firstVehicleValue, parseVehicleValue, vehicleOptionValue } from "../lib/vehicleValue";

type BuilderTransportSelection = {
  inventoryId: string;
  quantity: number;
  selectedVehicleType?: string | null;
  startTime?: string | null;
  durationHours?: number | null;
  serviceDates?: [Dayjs, Dayjs];
  startDate?: string | null;
  endDate?: string | null;
};

type BuilderFormValues = {
  startDate: Dayjs;
  nights: number;
  destinations: string[];
  adults: number;
  cwb: number;
  cnb: number;
  infant: number;
  childPassengers?: ChildPassenger[];
  comments?: string;
  conversionCurrency: string;
  conversionRate: number;
  currencyRate: number;
  hotels: Array<{
    inventoryId: string;
    stayDates: [Dayjs, Dayjs];
    roomType: string;
    mealPlan: string;
    numberOfRooms: number;
    aweb: number;
    cweb: number;
    cnb: number;
    infants: number;
  }>;
  days: Array<{
    day: number;
    activities: Array<{ inventoryId: string; quantity: number; selectedVehicleType?: string | null; itineraryItemKey?: string; attendance?: { adults: number; children: number; infants: number }; ageRateSelections?: PortalAgeSelection[]; agePassengerSnapshot?: string; agePricingRequestId?: string; reapplyAgePricing?: boolean }>;
    transports: BuilderTransportSelection[];
  }>;
  dailyTransports: BuilderTransportSelection[];
};

type EditingService =
  | { type: "hotel"; index: number }
  | { type: "activity" | "transport"; dayIndex: number; index: number }
  | { type: "fullDayTransport"; index: number };

const initialValues: BuilderFormValues = {
  startDate: dayjs().add(30, "day").startOf("day"),
  nights: 5,
  destinations: [],
  adults: 2,
  cwb: 0,
  cnb: 0,
  infant: 0,
  comments: "",
  conversionCurrency: "",
  conversionRate: 1,
  currencyRate: 1,
  hotels: [],
  days: Array.from({ length: 4 }, (_, day) => ({ day, activities: [], transports: [] })),
  dailyTransports: [],
};

const getRequestError = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError<{ error?: string }>(error)) return fallback;
  return error.response?.data?.error || fallback;
};

const findInventory = (items: PortalInventoryItem[] | undefined, id?: string) => items?.find((item) => item.id === id);

const inventoryOptions = (items: PortalInventoryItem[] | undefined) =>
  (items || []).map((item) => ({
    value: item.id,
    label: `${item.name} - ${item.city}, ${item.state || item.country}`,
    searchText: [item.name, item.city, item.country, item.state, item.description, item.category, item.serviceType]
      .filter(Boolean)
      .join(" "),
  }));

/** Matches a quick-add inventory option using the same forgiving rules as inventory browsing. */
const filterInventoryOption = (search: string, option?: { label?: unknown; searchText?: unknown }) =>
  matchesInventorySearch(search, [String(option?.searchText || option?.label || "")]);

const isGroupActivity = (item?: PortalInventoryItem) =>
  ["Transfers", "Private Tours", "Private Transfers"].includes(item?.category || "");

const activitySupportsVehicle = (item?: PortalInventoryItem) =>
  ["Private Tours", "Private Transfers"].includes(item?.category || "");

const transportHasEditableDuration = (item?: PortalInventoryItem) =>
  ["cab", "private_transfer", "private_tour"].includes(item?.serviceType || "");

/** Picks the smallest vehicle that seats the party (else the largest) and how many are needed. */
const pickVehicleForParty = (
  options: PortalInventoryItem["vehicleOptions"],
  party: number,
): { vehicleType: string | null; quantity: number } => {
  const usable = (options || []).filter((option) => option.vehicleType);
  if (!usable.length) return { vehicleType: null, quantity: 1 };
  const bySeats = [...usable].sort((left, right) => Number(left.pax) - Number(right.pax));
  const chosen = bySeats.find((option) => Number(option.pax) >= party) || bySeats[bySeats.length - 1];
  const capacity = Math.max(1, Number(chosen.pax) || 1);
  return { vehicleType: vehicleOptionValue(chosen), quantity: Math.max(1, Math.ceil(Math.max(party, 1) / capacity)) };
};

/**
 * Keeps a stored vehicle and count only when together they seat the party; otherwise fits one to the party,
 * so a template saved for a small group never leaves a large group in a single small vehicle.
 */
const vehicleForParty = (
  options: PortalInventoryItem["vehicleOptions"],
  party: number,
  stored?: { vehicleType?: string | null; quantity?: number | null },
): { vehicleType: string | null; quantity: number } => {
  const quantity = Math.max(1, Number(stored?.quantity || 1));
  const storedValue = parseVehicleValue(stored?.vehicleType);
  const storedOption = storedValue.vehicleType
    ? (options || []).find(
        (option) =>
          option.vehicleType === storedValue.vehicleType &&
          (storedValue.pax === null || Number(option.pax) === storedValue.pax) &&
          Number(option.pax) * quantity >= party,
      )
    : undefined;
  if (storedOption) return { vehicleType: vehicleOptionValue(storedOption), quantity };
  return pickVehicleForParty(options, party);
};

const normalizeRoomName = (value: string) =>
  value
    .toLocaleLowerCase()
    .replace(/\[\[.*?\]\]/g, " ")
    .replace(/\(.*?\)/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Resolves the room a template stored against the hotel's current room list. Suppliers rename rooms
 * (for example "Deluxe city view" becomes "Deluxe city view (2P)"), so an exact match is tried first and
 * then a match on the name without bracketed suffixes. Returns undefined when no single room matches.
 */
const resolveTemplateRoom = (
  inventory: PortalInventoryItem | undefined,
  roomType: string,
  mealPlan: string,
): { roomType: string; mealPlan: string } | undefined => {
  const options = inventory?.roomOptions || [];
  const target = normalizeRoomName(roomType);
  const exact = options.find((option) => option.roomType === roomType);
  const fuzzy = options.filter((option) => normalizeRoomName(option.roomType) === target);
  const room = exact || (fuzzy.length === 1 ? fuzzy[0] : undefined);
  if (!room) return undefined;
  return { roomType: room.roomType, mealPlan: room.mealPlans?.includes(mealPlan) ? mealPlan : room.mealPlans?.[0] || mealPlan };
};

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);

const normalizeDestination = (value: string) => value.trim().toLocaleLowerCase();

/**
 * Returns the 1-based numbers of days with no activities or transports. Reports nothing when every day is
 * empty, so a brand-new blank itinerary stays quiet and only gaps next to planned days are flagged.
 */
const getEmptyDayNumbers = (days: BuilderFormValues["days"] | undefined): number[] => {
  const list = days || [];
  const isEmpty = (day: BuilderFormValues["days"][number] | undefined) =>
    !day?.activities?.length && !day?.transports?.length;
  if (list.every(isEmpty)) return [];
  return list.flatMap((day, index) => (isEmpty(day) ? [index + 1] : []));
};

/** Formats day numbers for a message, collapsing runs: [5, 6, 7] becomes "Days 5-7". */
const formatEmptyDays = (dayNumbers: number[]): string => {
  if (dayNumbers.length === 1) return `Day ${dayNumbers[0]}`;
  const isRun = dayNumbers.every((day, index) => index === 0 || day === dayNumbers[index - 1] + 1);
  if (isRun && dayNumbers.length > 2) return `Days ${dayNumbers[0]}-${dayNumbers[dayNumbers.length - 1]}`;
  return `Days ${dayNumbers.slice(0, -1).join(", ")} and ${dayNumbers[dayNumbers.length - 1]}`;
};

const getRouteStopForDay = (routeStops: PortalRouteStop[], dayIndex: number) => {
  let startDay = 0;
  for (const [index, stop] of routeStops.entries()) {
    const stopNights = Math.max(1, Number(stop.nights || 1));
    const isFinalStop = index === routeStops.length - 1;
    if (dayIndex >= startDay && dayIndex <= startDay + stopNights - 1 + (isFinalStop ? 1 : 0)) return stop;
    startDay += stopNights;
  }
  return undefined;
};

const inventoryMatchesRouteStop = (item: PortalInventoryItem, stop?: PortalRouteStop) => {
  if (!stop) return false;
  const countryMatches = !item.country || [item.country, item.state].some((value) => normalizeDestination(value || "") === normalizeDestination(stop.country));
  const cityMatches = !stop.city || !item.city || normalizeDestination(item.city) === normalizeDestination(stop.city);
  return countryMatches && cityMatches;
};

/**
 * Converts portal form state into the cost-free API contract.
 * @param values Validated builder form values
 */
const buildPortalItineraryPayload = (values: BuilderFormValues): PortalItineraryPayload => ({
  startDate: dayjs(values.startDate).format("YYYY-MM-DD"),
  nights: Number(values.nights),
  destinations: values.destinations || [],
  adults: Number(values.adults),
  cwb: Number(values.cwb || 0),
  cnb: Number(values.cnb || 0),
  infant: Number(values.infant || 0),
  childPassengers: values.childPassengers || [],
  comments: values.comments?.trim() || "",
  conversionCurrency: values.conversionCurrency,
  conversionRate: Number(values.conversionRate || 1),
  currencyRate: Number(values.currencyRate || 1),
  hotels: (values.hotels || []).map((hotel) => ({
    inventoryId: hotel.inventoryId,
    checkInDate: dayjs(hotel.stayDates?.[0]).format("YYYY-MM-DD"),
    checkOutDate: dayjs(hotel.stayDates?.[1]).format("YYYY-MM-DD"),
    roomType: hotel.roomType,
    mealPlan: hotel.mealPlan,
    numberOfRooms: Number(hotel.numberOfRooms || 1),
    aweb: Number(hotel.aweb || 0),
    cweb: Number(hotel.cweb || 0),
    cnb: Number(hotel.cnb || 0),
    infants: Number(hotel.infants || 0),
  })),
  days: (values.days || []).map((day, dayIndex) => ({
    day: dayIndex,
    activities: (day.activities || []).map((activity) => ({
      inventoryId: activity.inventoryId,
      itineraryItemKey: activity.itineraryItemKey,
      attendance: activity.attendance,
      ageRateSelections: activity.ageRateSelections,
      agePassengerSnapshot: activity.agePassengerSnapshot,
      agePricingRequestId: activity.agePricingRequestId,
      reapplyAgePricing: activity.reapplyAgePricing,
      quantity: Number(activity.quantity || 1),
      selectedVehicleType: parseVehicleValue(activity.selectedVehicleType).vehicleType,
      selectedVehiclePax: parseVehicleValue(activity.selectedVehicleType).pax,
    })),
    transports: [...(dayIndex === 0 ? values.dailyTransports || [] : []), ...(day.transports || [])].map(
      (transport) => ({
        inventoryId: transport.inventoryId,
        quantity: Number(transport.quantity || 1),
        selectedVehicleType: parseVehicleValue(transport.selectedVehicleType).vehicleType,
        selectedVehiclePax: parseVehicleValue(transport.selectedVehicleType).pax,
        startTime: transport.startTime || null,
        durationHours:
          transport.durationHours === null || transport.durationHours === undefined
            ? null
            : Number(transport.durationHours),
        startDate: transport.startDate || transport.serviceDates?.[0]?.format("YYYY-MM-DD") || null,
        endDate: transport.endDate || transport.serviceDates?.[1]?.format("YYYY-MM-DD") || null,
      }),
    ),
  })),
});

const SectionHeading = ({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof CalendarDaysIcon;
  title: string;
  description: string;
}) => (
  <div className="mb-4 flex items-start gap-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-[#FBFBFB]">
      <Icon size={17} strokeWidth={1.8} className="text-(--brand-primary)" />
    </div>
    <div>
      <h2 className="text-sm font-semibold text-[#131B2A]">{title}</h2>
      <p className="mt-0.5 text-xs leading-5 text-gray-500">{description}</p>
    </div>
  </div>
);

const InventoryLoading = () => (
  <div className="rounded-lg border border-gray-200 bg-[#FBFBFB] p-3">
    <Skeleton active paragraph={{ rows: 2 }} title={false} />
  </div>
);

const InventoryProductHeader = ({ item, fallback }: { item?: PortalInventoryItem; fallback: string }) => (
  <div className="flex min-w-0 flex-1 items-start gap-3">
    <div className="shrink-0">
      <Image
        width={88}
        height={68}
        preview={false}
        src={item?.image || undefined}
        fallback={item?.type === "activity" ? "/images/activity.jpg" : "/images/no-image.jpg"}
        className="rounded-lg border border-gray-200 object-cover"
        alt={item?.name || fallback}
      />
    </div>
    <div className="min-w-0">
      <h3 className="truncate text-sm font-semibold text-[#131B2A]">{item?.name || fallback}</h3>
      {item ? (
        <>
          <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
            <MapPinIcon size={12} /> {item.city}, {item.state || item.country}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {item.category ? <Tag className="!m-0">{item.category}</Tag> : null}
            {item.serviceType ? <Tag className="!m-0">{item.serviceType.replace(/_/g, " ")}</Tag> : null}
            {item.duration ? <Tag className="!m-0">{item.duration} hrs</Tag> : null}
          </div>
          {item.description ? (
            <p className="mt-2 line-clamp-2 text-xs leading-5 text-gray-600">{item.description}</p>
          ) : null}
        </>
      ) : (
        <p className="mt-1 text-xs text-gray-500">Search below or browse available inventory.</p>
      )}
    </div>
  </div>
);

/** Renders the agency itinerary request builder and confirmation flow. */
const PortalItineraryBuilderPage = () => {
  const { id: editingItineraryId, leadId: routeLeadId } = useParams();
  const [searchParams] = useSearchParams();
  const linkedLeadId = routeLeadId || searchParams.get("leadId") || undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const { actor, organisationName } = useOutletContext<PortalWorkspaceContext>();
  const { message, modal } = AntApp.useApp();
  const [form] = Form.useForm<BuilderFormValues>();
  // Service configuration fields are edited in modals and can be temporarily
  // unregistered. Preserve them so the portal-safe total-price preview always
  // sends the complete itinerary configuration without exposing cost details.
  const values = Form.useWatch([], { form, preserve: true });
  const nights = Form.useWatch("nights", form) || 1;
  const startDate = Form.useWatch("startDate", form);
  const destinations = Form.useWatch("destinations", form) || [];
  const startingDestination = (location.state as { destination?: string } | null)?.destination;
  const [isTripDetailsModalOpen, setIsTripDetailsModalOpen] = useState(true);
  const [isDestinationDropdownOpen, setIsDestinationDropdownOpen] = useState(false);
  const [hasConfirmedTripDetails, setHasConfirmedTripDetails] = useState(false);
  const [confirmationValues, setConfirmationValues] = useState<BuilderFormValues | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [actionIntent, setActionIntent] = useState<"save" | "submit">("submit");
  const [guestName, setGuestName] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const allowNavigationRef = useRef(false);
  const [browserTarget, setBrowserTarget] = useState<{
    type: "hotel" | "activity" | "transport";
    dayIndex?: number;
    daily?: boolean;
    /** When set, the selected item replaces the service at this list index. */
    replaceIndex?: number;
  } | null>(null);
  const [editingService, setEditingService] = useState<EditingService | null>(null);
  const [browsedInventory, setBrowsedInventory] = useState<PortalInventoryItem[]>([]);
  const activityListOperationsByDayRef = useRef<Map<number, FormListOperation>>(new Map());
  const transportListOperationsByDayRef = useRef<Map<number, FormListOperation>>(new Map());
  const [isRoutePromptOpen, setIsRoutePromptOpen] = useState(false);
  const [isRoutePlannerOpen, setIsRoutePlannerOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [openTemplateAfterRouteFlow, setOpenTemplateAfterRouteFlow] = useState(false);
  const [routeStops, setRouteStops] = useState<PortalRouteStop[]>([]);
  const [routePlannerValue, setRoutePlannerValue] = useState<PortalRouteStop[]>([]);
  const previewSequence = useRef(0);
  const childOperationsRef = useRef<FormListOperation | null>(null);
  const linkedPassengersSeededRef = useRef(false);
  const appliedTemplatePlan = useRef<string | undefined>(undefined);
  const tripDetailsSnapshot = useRef<Partial<BuilderFormValues> | null>(null);

  const countriesQuery = usePortalCountriesQuery();
  const itineraryDefaultsQuery = usePortalItineraryDefaultsQuery();
  const hotelsQuery = usePortalInventoryQuery("hotel", destinations);
  const activitiesQuery = usePortalInventoryQuery("activity", destinations);
  const transportsQuery = usePortalInventoryQuery("transport", destinations);
  const hotelInventory = useMemo(
    () => [...(hotelsQuery.data || []), ...browsedInventory.filter((item) => item.type === "hotel")],
    [browsedInventory, hotelsQuery.data],
  );
  const activityInventory = useMemo(
    () => [...(activitiesQuery.data || []), ...browsedInventory.filter((item) => item.type === "activity")],
    [activitiesQuery.data, browsedInventory],
  );
  const transportInventory = useMemo(
    () => [...(transportsQuery.data || []), ...browsedInventory.filter((item) => item.type === "transport")],
    [browsedInventory, transportsQuery.data],
  );
  const activityIdsInPlan = useMemo(
    () => (values?.days || []).flatMap((day) => (day?.activities || []).map((activity) => activity.inventoryId)),
    [values?.days],
  );
  const linkedTransportsQuery = usePortalLinkedTransportsQuery(activityIdsInPlan);
  const partySize = Number(values?.adults || 0) + Number(values?.cwb || 0) + Number(values?.cnb || 0);
  const totalPriceMutation = usePortalTotalPriceMutation();
  // None of the chosen services has a price, so there is nothing to quote: hide the total and block saving.
  const isUnpriced = totalPriceMutation.data?.priced === false;
  const saveDraftMutation = useSavePortalItineraryDraftMutation();
  const submitMutation = useSubmitPortalItineraryMutation();
  const saveMarkupMutation = useSavePortalQuoteMarkupMutation();
  // The agent's private markup. It is saved with the quote and never sent to the supplier.
  const [markupDraft, setMarkupDraft] = useState<MarkupDraft>(emptyMarkupDraft);
  const [markupDirty, setMarkupDirty] = useState(false);
  const markupLoadedRef = useRef(false);
  const [markupModalOpen, setMarkupModalOpen] = useState(false);
  const draftQuery = usePortalItineraryDraftQuery(editingItineraryId);
  const duplicateMutation = useDuplicatePortalItineraryMutation();
  // Below lg the fixed 56px mobile header sits above the page; from lg up the sidebar layout has no top header.
  const isLargeScreen = useMediaQuery("(min-width: 1024px)");
  const sectionNavOffset = isLargeScreen ? 0 : 56;
  const [downloading, setDownloading] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const quoteMarkupQuery = usePortalQuoteMarkupQuery(editingItineraryId);
  useEffect(() => {
    if (markupLoadedRef.current || !quoteMarkupQuery.data) return;
    markupLoadedRef.current = true;
    // Do not overwrite values the agent has already started typing.
    setMarkupDraft((current) => (markupDirty ? current : markupToDraft(quoteMarkupQuery.data.markup)));
  }, [markupDirty, quoteMarkupQuery.data]);
  const linkedRequestQuery = usePortalTripRequestQuery(!editingItineraryId ? linkedLeadId : undefined);
  useEffect(() => {
    const lead = linkedRequestQuery.data;
    if (editingItineraryId || !lead || linkedPassengersSeededRef.current || isDirty) return;
    linkedPassengersSeededRef.current = true;
    if (lead.passengerCounts?.adults) form.setFieldsValue(lead.passengerCounts);
    if (lead.childPassengers?.length && childOperationsRef.current) {
      childOperationsRef.current.remove((form.getFieldValue("childPassengers") || []).map((_: unknown, index: number) => index));
      lead.childPassengers.forEach((child) => childOperationsRef.current!.add(child));
    }
  }, [editingItineraryId, linkedRequestQuery.data, isDirty, form]);
  const portalPaths = getPortalPaths();
  const templatePlan = (location.state as { templatePlan?: PortalTemplatePlan } | null)?.templatePlan;
  const itineraryCurrency = itineraryDefaultsQuery.data?.currency || "INR";
  const sellingCurrency = Form.useWatch("conversionCurrency", form) || itineraryCurrency;
  // The tenant chooses which currencies agents may quote in; a saved quote keeps its own currency even if later disabled.
  const sellingCurrencyOptions = useMemo(() => {
    const configured = itineraryDefaultsQuery.data?.sellingCurrencies || [{ currency: itineraryCurrency, rate: 1 }];
    return configured.some((option) => option.currency === sellingCurrency)
      ? configured
      : [...configured, { currency: sellingCurrency, rate: Number(values?.currencyRate || 1) }];
  }, [itineraryCurrency, itineraryDefaultsQuery.data?.sellingCurrencies, sellingCurrency, values?.currencyRate]);

  // A destination chosen on the dashboard (AI assistant unavailable) prefills the trip details popup.
  useEffect(() => {
    if (!startingDestination || editingItineraryId || (form.getFieldValue("destinations") || []).length) return;
    form.setFieldValue("destinations", [startingDestination]);
  }, [editingItineraryId, form, startingDestination]);

  useEffect(() => {
    const defaults = itineraryDefaultsQuery.data;
    if (!defaults?.currency || form.getFieldValue("conversionCurrency")) return;
    const defaultCurrency = defaults.defaultSellingCurrency || defaults.currency;
    const defaultOption = defaults.sellingCurrencies.find((option) => option.currency === defaultCurrency);
    form.setFieldsValue({
      conversionCurrency: defaultOption ? defaultCurrency : defaults.currency,
      conversionRate: 1,
      currencyRate: defaultOption?.rate || 1,
    });
  }, [form, itineraryDefaultsQuery.data]);

  useEffect(() => {
    const payload = draftQuery.data?.itinerary;
    if (!payload) return;
    const dailyTransports = payload.days.flatMap((day) =>
      day.transports
        .filter((transport) => Boolean(transport.startDate && transport.endDate))
        .map((transport) => ({
          ...transport,
          serviceDates: [dayjs(transport.startDate), dayjs(transport.endDate)] as [Dayjs, Dayjs],
        })),
    );
    const { childPassengers, ...tripFields } = payload;
    const childOperations = childOperationsRef.current;
    if (childOperations && childPassengers) {
      childOperations.remove((form.getFieldValue("childPassengers") || []).map((_: unknown, index: number) => index));
      childPassengers.forEach((child) => childOperations.add(child));
    }
    form.setFieldsValue({
      ...tripFields,
      dailyTransports,
      days: payload.days.map((day) => ({
        ...day,
        activities: day.activities.map(({ ageRateSelections: _selections, ...activity }) => activity),
        transports: day.transports.filter((transport) => !(transport.startDate && transport.endDate)),
      })),
      startDate: dayjs(payload.startDate),
      hotels: payload.hotels.map((hotel) => ({
        ...hotel,
        stayDates: [dayjs(hotel.checkInDate), dayjs(hotel.checkOutDate)] as [Dayjs, Dayjs],
      })),
    });
    setHasConfirmedTripDetails(true);
    setIsTripDetailsModalOpen(false);
  }, [draftQuery.data, form]);

  useEffect(() => {
    // A saved draft (including one created by the AI assistant) can reference products outside the
    // first page of destination inventory. Resolve them by id so rows show real names, not "details required".
    const payload = draftQuery.data?.itinerary;
    if (!payload) return;
    const refs: Array<{ type: PortalInventoryType; id: string }> = [
      ...payload.hotels.map((hotel) => ({ type: "hotel" as const, id: hotel.inventoryId })),
      ...payload.days.flatMap((day) => [
        ...day.activities.map((activity) => ({ type: "activity" as const, id: activity.inventoryId })),
        ...day.transports.map((transport) => ({ type: "transport" as const, id: transport.inventoryId })),
      ]),
    ];
    if (!refs.length) return;
    let cancelled = false;
    resolvePortalInventoryItems(refs)
      .then((items) => {
        if (cancelled || !items.length) return;
        setBrowsedInventory((current) => [
          ...current,
          ...items.filter((item) => !current.some((existing) => existing.id === item.id)),
        ]);
      })
      .catch((error) => console.error("Unable to resolve saved inventory", error));
    return () => {
      cancelled = true;
    };
  }, [draftQuery.data]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && !allowNavigationRef.current && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    modal.confirm({
      title: "Leave without saving?",
      content: "Your unsaved itinerary changes will be lost.",
      okText: "Leave page",
      cancelText: "Keep editing",
      okButtonProps: { danger: true },
      onOk: () => {
        allowNavigationRef.current = true;
        blocker.proceed();
      },
      onCancel: () => blocker.reset(),
    });
  }, [blocker, modal]);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!isDirty || allowNavigationRef.current) return;
        event.preventDefault();
      },
      [isDirty],
    ),
  );

  const cancelNewItinerary = useCallback(() => {
    if (location.key !== "default" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(portalPaths.itineraries, { replace: true });
  }, [location.key, navigate, portalPaths.itineraries]);

  const openTripDetailsEditor = () => {
    const currentValues = form.getFieldsValue(true) as BuilderFormValues;
    tripDetailsSnapshot.current = {
      startDate: currentValues.startDate,
      nights: currentValues.nights,
      destinations: [...(currentValues.destinations || [])],
      adults: currentValues.adults,
      cwb: currentValues.cwb,
      cnb: currentValues.cnb,
      infant: currentValues.infant,
      comments: currentValues.comments,
      conversionCurrency: currentValues.conversionCurrency,
      conversionRate: currentValues.conversionRate,
      currencyRate: currentValues.currencyRate,
    };
    setIsDestinationDropdownOpen(false);
    setIsTripDetailsModalOpen(true);
  };

  const cancelTripDetails = () => {
    if (!hasConfirmedTripDetails) {
      cancelNewItinerary();
      return;
    }
    if (tripDetailsSnapshot.current) form.setFieldsValue(tripDetailsSnapshot.current);
    tripDetailsSnapshot.current = null;
    setIsDestinationDropdownOpen(false);
    setIsTripDetailsModalOpen(false);
  };

  const confirmTripDetails = async () => {
    try {
      await form.validateFields(["startDate", "nights", "destinations", "adults", "cwb", "cnb", "infant"]);
      const isInitialConfirmation = !hasConfirmedTripDetails;
      const previousDetails = tripDetailsSnapshot.current;
      const nextDetails = form.getFieldsValue(["nights", "destinations"]);
      const previousDestinations = (previousDetails?.destinations || []).map(normalizeDestination).sort();
      const nextDestinations = (nextDetails.destinations || []).map(normalizeDestination).sort();
      if (
        previousDetails &&
        (Number(previousDetails.nights) !== Number(nextDetails.nights) ||
          JSON.stringify(previousDestinations) !== JSON.stringify(nextDestinations))
      ) {
        if (routeStops.length > 0) {
          message.warning("Trip details changed. Route stops were kept; use Plan route stops to adjust them.");
        }
      }
      setHasConfirmedTripDetails(true);
      tripDetailsSnapshot.current = null;
      setIsDestinationDropdownOpen(false);
      setIsTripDetailsModalOpen(false);
      if (isInitialConfirmation) {
        setOpenTemplateAfterRouteFlow(true);
        openRoutePlanner();
      }
    } catch {
      // Ant Design renders the relevant field errors inside the modal.
    }
  };

  const openRoutePlanner = () => {
    if (routeStops.length === 0 && destinations.length > 0) {
      const initialRouteNights = Math.max(Number(nights), destinations.length);
      const baseNights = Math.floor(initialRouteNights / destinations.length);
      const remainder = initialRouteNights % destinations.length;
      setRoutePlannerValue(
        destinations.map((country, index) => ({
          country,
          city: "",
          nights: baseNights + (index < remainder ? 1 : 0),
        })),
      );
    } else {
      setRoutePlannerValue(routeStops);
    }
    setIsRoutePlannerOpen(true);
  };

  const isFlowPageOpen = isRoutePlannerOpen || isTemplateModalOpen;

  const routeCountrySet = new Set(routeStops.map((stop) => normalizeDestination(stop.country)).filter(Boolean));
  const destinationCountrySet = new Set(destinations.map(normalizeDestination).filter(Boolean));
  const hasCompleteCityRoute =
    routeStops.length > 0 &&
    routeStops.every((stop) => stop.country && stop.city && Number(stop.nights) > 0) &&
    routeStops.reduce((sum, stop) => sum + Number(stop.nights), 0) === Number(nights) &&
    routeCountrySet.size === destinationCountrySet.size &&
    Array.from(routeCountrySet).every((country) => destinationCountrySet.has(country));

  const warnForRouteMismatches = useCallback(
    (entries: Array<{ inventory: PortalInventoryItem; dayIndex?: number }>) => {
      if (!routeStops.length) return;
      const mismatches = entries.filter(({ inventory, dayIndex }) => {
        if (dayIndex !== undefined)
          return !inventoryMatchesRouteStop(inventory, getRouteStopForDay(routeStops, dayIndex));
        return !routeStops.some((stop) => inventoryMatchesRouteStop(inventory, stop));
      });
      if (!mismatches.length) return;
      const first = mismatches[0];
      const plannedStop = first.dayIndex !== undefined ? getRouteStopForDay(routeStops, first.dayIndex) : undefined;
      message.warning(
        `${mismatches.length === 1 ? first.inventory.name : `${mismatches.length} items`} ${mismatches.length === 1 ? "does" : "do"} not match ${plannedStop?.city || plannedStop?.country || "the planned route"}. ${mismatches.length === 1 ? "It was" : "They were"} added without changing route stops.`,
      );
    },
    [message, routeStops],
  );

  const openQuoteRecommendations = () => {
    if (hasCompleteCityRoute) {
      setIsTemplateModalOpen(true);
      return;
    }
    setOpenTemplateAfterRouteFlow(true);
    setIsRoutePromptOpen(true);
  };

  const finishRouteFlow = () => {
    setIsRoutePlannerOpen(false);
    if (openTemplateAfterRouteFlow) {
      setOpenTemplateAfterRouteFlow(false);
      setIsTemplateModalOpen(true);
    }
  };

  /** Applies a resolved template plan, preserving an in-progress builder when opened in-context. */
  const applyTemplatePlanToBuilder = useCallback(
    (plan: PortalTemplatePlan, replaceExisting = false, showMessage = true) => {
      const templateInventory = [
        ...plan.hotels.map((hotel) => hotel.inventory),
        ...(plan.dailyTransports || []).map((transport) => transport.inventory),
        ...plan.days.flatMap((entry) => [
          ...entry.activities.map((activity) => activity.inventory),
          ...entry.transports.map((transport) => transport.inventory),
        ]),
      ];
      setBrowsedInventory((current) => [
        ...current,
        ...templateInventory.filter((item) => !current.some((existing) => existing.id === item.id)),
      ]);

      const currentValues = form.getFieldsValue(true) as Partial<BuilderFormValues>;
      const selectedStartDate = currentValues.startDate || initialValues.startDate;
      const nextNights = replaceExisting
        ? plan.nights
        : Math.max(Number(currentValues.nights || 1), Number(plan.nights || 1));
      const nextDestinations = replaceExisting
        ? plan.destinations
        : Array.from(new Set([...(currentValues.destinations || []), ...plan.destinations]));
      const party =
        Number(currentValues.adults || 0) + Number(currentValues.cwb || 0) + Number(currentValues.cnb || 0);
      const staleRoomHotels: string[] = [];
      const templateHotels: BuilderFormValues["hotels"] = plan.hotels.map((hotel) => {
        // Templates store the room name at save time; re-resolve it against the hotel's current rooms so a renamed
        // room does not leave the quote unpriceable.
        const room = resolveTemplateRoom(hotel.inventory, hotel.roomType, hotel.mealPlan);
        if (!room) staleRoomHotels.push(hotel.inventory.name);
        return {
          inventoryId: hotel.hotelId,
          stayDates: [
            dayjs(selectedStartDate).add(hotel.dayOffset, "day"),
            dayjs(selectedStartDate).add(hotel.dayOffset + hotel.nights, "day"),
          ],
          roomType: room?.roomType ?? "",
          mealPlan: room?.mealPlan ?? hotel.mealPlan,
          numberOfRooms: Number(hotel.numberOfRooms || 1),
          aweb: 0,
          cweb: 0,
          cnb: 0,
          infants: 0,
        };
      });
      const currentDays = replaceExisting ? [] : currentValues.days || [];
      const days = Array.from({ length: nextNights + 1 }, (_, day) => {
        const currentDay = currentDays[day];
        const templateDay = plan.days.find((entry) => entry.day === day);
        return {
          day,
          activities: [
            ...(currentDay?.activities || []),
            ...(templateDay?.activities || []).map((activity) => ({
              inventoryId: activity.activityId,
              quantity: Number(activity.quantity || 1),
              selectedVehicleType: activitySupportsVehicle(activity.inventory)
                ? vehicleForParty(activity.inventory.vehicleOptions, party, { vehicleType: activity.selectedVehicleType, quantity: 1 }).vehicleType
                : activity.selectedVehicleType || null,
            })),
          ],
          transports: [
            ...(currentDay?.transports || []),
            ...(templateDay?.transports || []).map((transport) => {
              // Vehicle-priced transfers have no base rate, so a template without a stored vehicle would price at zero.
              // Preselect the vehicle that fits the party, exactly as adding the transfer by hand does.
              const fallback = vehicleForParty(transport.inventory.vehicleOptions, party, {
                vehicleType: transport.selectedVehicleType,
                quantity: transport.quantity,
              });
              return {
                inventoryId: transport.transportId,
                quantity: fallback.quantity,
                selectedVehicleType: fallback.vehicleType,
                startTime: transport.startTime || null,
                durationHours: transport.durationHours ?? null,
              };
            }),
          ],
        };
      });
      const templateDailyTransports: BuilderFormValues["dailyTransports"] = (plan.dailyTransports || []).map(
        (transport) => {
          const serviceStart = dayjs(selectedStartDate).add(transport.startDay, "day");
          const serviceEnd = dayjs(selectedStartDate).add(transport.endDay, "day");
          const vehicle = vehicleForParty(transport.inventory.vehicleOptions, party, {
            vehicleType: transport.selectedVehicleType,
            quantity: transport.quantity,
          });
          return {
            inventoryId: transport.transportId,
            quantity: vehicle.quantity,
            selectedVehicleType: vehicle.vehicleType,
            serviceDates: [serviceStart, serviceEnd],
            startDate: serviceStart.format("YYYY-MM-DD"),
            endDate: serviceEnd.format("YYYY-MM-DD"),
          };
        },
      );

      form.setFieldsValue({
        nights: nextNights,
        destinations: nextDestinations,
        hotels: replaceExisting ? templateHotels : [...(currentValues.hotels || []), ...templateHotels],
        days,
        dailyTransports: replaceExisting
          ? templateDailyTransports
          : [...(currentValues.dailyTransports || []), ...templateDailyTransports],
      });
      warnForRouteMismatches([
        ...plan.hotels.map((hotel) => ({ inventory: hotel.inventory, dayIndex: hotel.dayOffset })),
        ...plan.days.flatMap((entry) => [
          ...entry.activities.map((activity) => ({ inventory: activity.inventory, dayIndex: entry.day })),
          ...entry.transports.map((transport) => ({ inventory: transport.inventory, dayIndex: entry.day })),
        ]),
      ]);
      if (staleRoomHotels.length) {
        message.warning(
          `Select a room for ${Array.from(new Set(staleRoomHotels)).join(", ")}: the package's room is no longer offered.`,
          6,
        );
      }
      if (showMessage) {
        message.success(
          replaceExisting ? "Package itinerary added to the builder" : "Package plan applied to the itinerary",
        );
      }
    },
    [form, message, warnForRouteMismatches],
  );

  useEffect(() => {
    if (!templatePlan) return;
    const signature = JSON.stringify(templatePlan);
    if (appliedTemplatePlan.current === signature) return;
    appliedTemplatePlan.current = signature;
    applyTemplatePlanToBuilder(templatePlan, true);
    navigate(location.pathname, { replace: true, state: null });
  }, [applyTemplatePlanToBuilder, location.pathname, navigate, templatePlan]);

  useEffect(() => {
    // Applying a template updates its nights and day plan together. Wait for
    // those form values before normalizing the visible day editors.
    if (templatePlan || isTripDetailsModalOpen) return;
    const currentDays = form.getFieldValue("days") || [];
    const expectedCount = Math.max(2, Number(nights) + 1);
    if (currentDays.length === expectedCount) return;
    form.setFieldValue(
      "days",
      Array.from({ length: expectedCount }, (_, day) => ({
        day,
        activities: currentDays[day]?.activities || [],
        transports: currentDays[day]?.transports || [],
      })),
    );
  }, [form, isTripDetailsModalOpen, nights, templatePlan]);

  const hasItems = useMemo(
    () =>
      Boolean(
        values?.hotels?.length ||
        values?.dailyTransports?.length ||
        values?.days?.some((day) => day?.activities?.length || day?.transports?.length),
      ),
    [values],
  );

  const emptyDayNumbers = useMemo(() => getEmptyDayNumbers(values?.days), [values?.days]);

  const routeConflictWarnings = useMemo(() => {
    if (!routeStops.length) return [];
    const conflicts: string[] = [];
    (values?.hotels || []).forEach((hotel) => {
      const inventory = findInventory(hotelInventory, hotel.inventoryId);
      if (inventory && !routeStops.some((stop) => inventoryMatchesRouteStop(inventory, stop))) {
        conflicts.push(`${inventory.name} is outside the planned route.`);
      }
    });
    (values?.days || []).forEach((day, dayIndex) => {
      const stop = getRouteStopForDay(routeStops, dayIndex);
      (day.activities || []).forEach((activity) => {
        const inventory = findInventory(activityInventory, activity.inventoryId);
        if (inventory && !inventoryMatchesRouteStop(inventory, stop)) {
          conflicts.push(`${inventory.name} does not match ${stop?.city || stop?.country || `day ${dayIndex + 1}`}.`);
        }
      });
      (day.transports || []).forEach((transport) => {
        const inventory = findInventory(transportInventory, transport.inventoryId);
        if (inventory && !inventoryMatchesRouteStop(inventory, stop)) {
          conflicts.push(`${inventory.name} does not match ${stop?.city || stop?.country || `day ${dayIndex + 1}`}.`);
        }
      });
    });
    return Array.from(new Set(conflicts));
  }, [activityInventory, hotelInventory, routeStops, transportInventory, values?.days, values?.hotels]);

  const inventoryTerms = useMemo(() => {
    const selectedItems = [
      ...(values?.hotels || []).map((hotel) => findInventory(hotelInventory, hotel.inventoryId)),
      ...(values?.days || []).flatMap((day) => [
        ...(day?.activities || []).map((activity) => findInventory(activityInventory, activity.inventoryId)),
        ...(day?.transports || []).map((transport) => findInventory(transportInventory, transport.inventoryId)),
      ]),
      ...(values?.dailyTransports || []).map((transport) => findInventory(transportInventory, transport.inventoryId)),
    ].filter((item): item is PortalInventoryItem => Boolean(item));
    const capitalizeTerm = (value: string) =>
      value ? value.charAt(0).toUpperCase() + value.substring(1).toLowerCase() : "";
    const uniqueTerms = (key: "inclusions" | "exclusions") =>
      Array.from(
        new Map(
          [...(itineraryDefaultsQuery.data?.[key] || []), ...selectedItems.flatMap((item) => item[key] || [])]
            .map((term) => capitalizeTerm(term.trim()))
            .filter(Boolean)
            .map((term) => [term.toLocaleLowerCase(), term]),
        ).values(),
      );
    return { inclusions: uniqueTerms("inclusions"), exclusions: uniqueTerms("exclusions") };
  }, [
    activityInventory,
    hotelInventory,
    itineraryDefaultsQuery.data,
    transportInventory,
    values?.days,
    values?.dailyTransports,
    values?.hotels,
  ]);

  const hotelOccupancySignature = useMemo(
    () =>
      JSON.stringify({
        adults: values?.adults || 0,
        cwb: values?.cwb || 0,
        cnb: values?.cnb || 0,
        infant: values?.infant || 0,
        hotels: (values?.hotels || []).map((hotel) => ({
          inventoryId: hotel?.inventoryId,
          roomType: hotel?.roomType,
          numberOfRooms: hotel?.numberOfRooms,
        })),
      }),
    [values?.adults, values?.cnb, values?.cwb, values?.hotels, values?.infant],
  );

  useEffect(() => {
    const currentHotels = (form.getFieldValue("hotels") || []) as BuilderFormValues["hotels"];
    let changed = false;
    const nextHotels = currentHotels.map((hotel) => {
      const inventory = findInventory(hotelInventory, hotel.inventoryId);
      const room = inventory?.roomOptions?.find((option) => option.roomType === hotel.roomType);
      if (!room) return hotel;
      const roomCapacity = Math.max(1, Number(room.paxPerRoom || 1)) * Math.max(1, Number(hotel.numberOfRooms || 1));
      const occupancy = {
        aweb: Math.max(0, Number(values?.adults || 0) - roomCapacity),
        cweb: Math.max(0, Number(values?.cwb || 0)),
        cnb: Math.max(0, Number(values?.cnb || 0)),
        infants: Math.max(0, Number(values?.infant || 0)),
      };
      if (
        Number(hotel.aweb || 0) === occupancy.aweb &&
        Number(hotel.cweb || 0) === occupancy.cweb &&
        Number(hotel.cnb || 0) === occupancy.cnb &&
        Number(hotel.infants || 0) === occupancy.infants
      ) {
        return hotel;
      }
      changed = true;
      return { ...hotel, ...occupancy };
    });
    if (changed) form.setFieldValue("hotels", nextHotels);
  }, [form, hotelOccupancySignature, hotelInventory, values?.adults, values?.cnb, values?.cwb, values?.infant]);

  useEffect(() => {
    if (!values || !hasItems || !values.startDate || !values.destinations?.length || !values.adults) {
      totalPriceMutation.reset();
      return;
    }
    const sequence = ++previewSequence.current;
    const timer = window.setTimeout(async () => {
      try {
        const payload = buildPortalItineraryPayload(values as BuilderFormValues);
        await form.validateFields({ validateOnly: true });
        if (sequence === previewSequence.current) {
          totalPriceMutation.mutate({ payload, itineraryId: editingItineraryId });
        }
      } catch {
        if (sequence === previewSequence.current) totalPriceMutation.reset();
      }
    }, 650);
    return () => window.clearTimeout(timer);
    // Serialized form values intentionally drive the debounced total refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values), hasItems]);

  const guestCount = Number(values?.adults || 0) + Number(values?.cwb || 0) + Number(values?.cnb || 0);
  // Guests are often changed after a template or transfer is added, so refit any vehicle that no longer seats the party.
  useEffect(() => {
    if (guestCount <= 0) return;
    const untypedForm = form as FormInstance;
    const current = untypedForm.getFieldsValue(true) as Partial<BuilderFormValues>;
    const refit = (path: Array<string | number>, service: { inventoryId: string; quantity?: number; selectedVehicleType?: string | null }) => {
      const options = findInventory(transportInventory, service.inventoryId)?.vehicleOptions;
      if (!options?.length) return;
      const fit = vehicleForParty(options, guestCount, { vehicleType: service.selectedVehicleType, quantity: service.quantity });
      if (fit.vehicleType !== (service.selectedVehicleType ?? null)) untypedForm.setFieldValue([...path, "selectedVehicleType"], fit.vehicleType);
      if (fit.quantity !== Number(service.quantity || 1)) untypedForm.setFieldValue([...path, "quantity"], fit.quantity);
    };
    (current.days || []).forEach((day, dayIndex) =>
      (day?.transports || []).forEach((transport, index) => refit(["days", dayIndex, "transports", index], transport)),
    );
    (current.dailyTransports || []).forEach((transport, index) => refit(["dailyTransports", index], transport));
    // Only guest-count or inventory changes should refit; manual vehicle picks that still seat the party are kept.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestCount, transportInventory]);

  /** Switches a transport's vehicle from its list card and resizes the vehicle count to seat the party. */
  const changeTransportVehicle = (
    path: Array<string | number>,
    inventory: PortalInventoryItem | undefined,
    vehicleType?: string,
  ) => {
    const untypedForm = form as FormInstance;
    untypedForm.setFieldValue([...path, "selectedVehicleType"], vehicleType || null);
    const vehicle = inventory?.vehicleOptions?.find((option) => vehicleOptionValue(option) === vehicleType);
    if (!vehicle?.pax) return;
    const party =
      Number(form.getFieldValue("adults") || 0) +
      Number(form.getFieldValue("cwb") || 0) +
      Number(form.getFieldValue("cnb") || 0);
    untypedForm.setFieldValue(
      [...path, "quantity"],
      Math.max(1, Math.ceil(Math.max(party, 1) / Math.max(1, Number(vehicle.pax)))),
    );
  };

  const addPortalTransports = (
    transports: PortalInventoryItem[],
    dayIndex: number,
    linkedActivities?: Map<string, PortalInventoryItem[]>,
    existingIndex?: number,
  ) => {
    const transportOperations = transportListOperationsByDayRef.current.get(dayIndex);
    const operations = activityListOperationsByDayRef.current.get(dayIndex);
    if (!operations || !transportOperations) return;
    const currentActivities = (form.getFieldValue(["days", dayIndex, "activities"]) ||
      []) as BuilderFormValues["days"][number]["activities"];
    const party =
      Number(form.getFieldValue("adults") || 0) +
      Number(form.getFieldValue("cwb") || 0) +
      Number(form.getFieldValue("cnb") || 0);
    transports.forEach((transport, transportIndex) => {
      // Preselect the vehicle that fits the party so a new transfer is usable straight away.
      const defaultVehicle = pickVehicleForParty(transport.vehicleOptions, party);
      const transportEntry = {
        inventoryId: transport.id,
        quantity: defaultVehicle.quantity,
        selectedVehicleType: defaultVehicle.vehicleType,
        ...(transportHasEditableDuration(transport) ? { durationHours: transport.duration || undefined } : {}),
      };
      if (existingIndex === undefined || transportIndex > 0) transportOperations.add(transportEntry);
      (linkedActivities?.get(transport.id) || []).forEach((activity) => {
        const existingActivityIndex = currentActivities.findIndex((item) => item.inventoryId === activity.id);
        if (existingActivityIndex >= 0) return;
        const entry = {
          inventoryId: activity.id,
          quantity: 1,
          selectedVehicleType: firstVehicleValue(activity.vehicleOptions) || null,
        };
        operations.add(entry);
        currentActivities.push(entry);
      });
    });
    if (linkedActivities) {
      const resolvedActivities = Array.from(linkedActivities.values()).flat();
      setBrowsedInventory((current) => [
        ...current,
        ...resolvedActivities.filter((activity) => !current.some((existing) => existing.id === activity.id)),
      ]);
    }
  };

  const addBrowsedInventory = (items: PortalInventoryItem[]) => {
    if (!browserTarget) return;
    setBrowsedInventory((current) => [
      ...current,
      ...items.filter((item) => !current.some((existing) => existing.id === item.id)),
    ]);
    if (browserTarget.type === "hotel") {
      const currentHotels = (form.getFieldValue("hotels") || []) as BuilderFormValues["hotels"];
      const existingIds = new Set(currentHotels.map((hotel) => hotel.inventoryId));
      const addedItems = items.filter((item) => !existingIds.has(item.id));
      form.setFieldValue("hotels", [
        ...currentHotels,
        ...addedItems.map((item) => {
          const room = item.roomOptions?.[0];
          return {
            inventoryId: item.id,
            stayDates: [startDate || dayjs(), dayjs(startDate || dayjs()).add(Number(nights), "day")],
            roomType: room?.roomType,
            mealPlan: room?.mealPlans?.[0],
            numberOfRooms: 1,
            aweb: 0,
            cweb: 0,
            cnb: 0,
            infants: 0,
          };
        }),
      ]);
      warnForRouteMismatches(addedItems.map((inventory) => ({ inventory })));
      return;
    }
    if (browserTarget.type === "transport" && browserTarget.daily) {
      const current = (form.getFieldValue("dailyTransports") || []) as BuilderFormValues["dailyTransports"];
      const existingIds = new Set(current.map((item) => item.inventoryId));
      const tripStart = dayjs(form.getFieldValue("startDate"));
      const tripEnd = tripStart.add(Number(form.getFieldValue("nights") || 0), "day");
      const partySize =
        Number(form.getFieldValue("adults") || 0) + Number(form.getFieldValue("cwb") || 0) + Number(form.getFieldValue("cnb") || 0);
      form.setFieldValue("dailyTransports", [
        ...current,
        ...items
          .filter((item) => !existingIds.has(item.id))
          .map((item) => {
            const vehicle = pickVehicleForParty(item.vehicleOptions, partySize);
            return {
            inventoryId: item.id,
            quantity: vehicle.quantity,
            selectedVehicleType: vehicle.vehicleType,
            serviceDates: [tripStart, tripEnd] as [Dayjs, Dayjs],
            startDate: tripStart.format("YYYY-MM-DD"),
            endDate: tripEnd.format("YYYY-MM-DD"),
            };
          }),
      ]);
      return;
    }
    const dayIndex = browserTarget.dayIndex ?? 0;
    const day = (form.getFieldValue(["days", dayIndex]) || {
      day: dayIndex,
      activities: [],
      transports: [],
    }) as BuilderFormValues["days"][number];
    if (browserTarget.replaceIndex !== undefined) {
      const [replacement] = items;
      if (!replacement) return;
      const listKey = browserTarget.type === "activity" ? "activities" : "transports";
      const siblings = (day[listKey] || []) as Array<{ inventoryId: string }>;
      if (siblings.some((item, index) => index !== browserTarget.replaceIndex && item.inventoryId === replacement.id)) {
        message.info(`This ${browserTarget.type} is already added to day ${dayIndex + 1}`);
        return;
      }
      const replaceIndex = browserTarget.replaceIndex;
      if (browserTarget.type === "activity") {
        form.setFieldValue(["days", dayIndex, "activities", replaceIndex, "inventoryId"], replacement.id);
        form.setFieldValue(
          ["days", dayIndex, "activities", replaceIndex, "selectedVehicleType"],
          activitySupportsVehicle(replacement) ? firstVehicleValue(replacement.vehicleOptions) || null : null,
        );
      } else {
        form.setFieldValue(["days", dayIndex, "transports", replaceIndex, "inventoryId"], replacement.id);
        form.setFieldValue(["days", dayIndex, "transports", replaceIndex, "selectedVehicleType"], null);
        form.setFieldValue(
          ["days", dayIndex, "transports", replaceIndex, "durationHours"],
          transportHasEditableDuration(replacement) ? replacement.duration || undefined : undefined,
        );
      }
      warnForRouteMismatches([{ inventory: replacement, dayIndex }]);
      return;
    }
    if (browserTarget.type === "activity") {
      const existingIds = new Set((day.activities || []).map((item) => item.inventoryId));
      const operations = activityListOperationsByDayRef.current.get(dayIndex);
      const addedItems = items.filter((item) => !existingIds.has(item.id));
      addedItems.forEach((item) => {
        operations?.add({ inventoryId: item.id, quantity: 1 });
      });
      warnForRouteMismatches(addedItems.map((inventory) => ({ inventory, dayIndex })));
    } else {
      const existingIds = new Set((day.transports || []).map((item) => item.inventoryId));
      const transportsToAdd = items.filter((item) => !existingIds.has(item.id));
      addPortalTransports(transportsToAdd, dayIndex);
      warnForRouteMismatches(transportsToAdd.map((inventory) => ({ inventory, dayIndex })));
    }
  };

  const addBrowsedTransportsWithLinkedActivities = (
    items: PortalInventoryItem[],
    linkedActivities: Map<string, PortalInventoryItem[]>,
  ) => {
    if (browserTarget?.type !== "transport") return;
    setBrowsedInventory((current) => [
      ...current,
      ...items.filter((item) => !current.some((existing) => existing.id === item.id)),
    ]);
    const dayIndex = browserTarget.dayIndex ?? 0;
    const currentTransports = (form.getFieldValue(["days", dayIndex, "transports"]) ||
      []) as BuilderFormValues["days"][number]["transports"];
    const existingIds = new Set(currentTransports.map((item) => item.inventoryId));
    addPortalTransports(
      items.filter((item) => !existingIds.has(item.id)),
      dayIndex,
      linkedActivities,
    );
  };

  const quickAddHotel = (inventoryId: string) => {
    const item = findInventory(hotelInventory, inventoryId);
    if (!item) return;
    const currentHotels = (form.getFieldValue("hotels") || []) as BuilderFormValues["hotels"];
    if (currentHotels.some((hotel) => hotel.inventoryId === inventoryId)) {
      message.info("This hotel is already in the itinerary");
      return;
    }
    const room = item.roomOptions?.[0];
    form.setFieldValue("hotels", [
      ...currentHotels,
      {
        inventoryId,
        stayDates: [startDate || dayjs(), dayjs(startDate || dayjs()).add(Number(nights), "day")],
        roomType: room?.roomType || "",
        mealPlan: room?.mealPlans?.[0] || "",
        numberOfRooms: 1,
        aweb: 0,
        cweb: 0,
        cnb: 0,
        infants: 0,
      },
    ]);
    warnForRouteMismatches([{ inventory: item }]);
  };

  /** Switches an activity between shared (SIC) and private by adding or removing its linked private transport. */
  const setActivityServiceMode = (
    dayIndex: number,
    linkedTransports: PortalInventoryItem[],
    mode: "sic" | "private",
  ) => {
    const operations = transportListOperationsByDayRef.current.get(dayIndex);
    if (!operations || !linkedTransports.length) return;
    const linkedIds = new Set(linkedTransports.map((transport) => transport.id));
    const dayTransports = (form.getFieldValue(["days", dayIndex, "transports"]) ||
      []) as BuilderFormValues["days"][number]["transports"];
    const existingIndexes = dayTransports.flatMap((transport, index) =>
      linkedIds.has(transport.inventoryId) ? [index] : [],
    );
    if (mode === "sic") {
      if (existingIndexes.length) operations.remove(existingIndexes);
      return;
    }
    if (existingIndexes.length) return;
    const [transport] = linkedTransports;
    setBrowsedInventory((current) =>
      current.some((item) => item.id === transport.id) ? current : [...current, transport],
    );
    addPortalTransports([transport], dayIndex);
    warnForRouteMismatches([{ inventory: transport, dayIndex }]);
  };

  const quickAddDayService = (type: "activity" | "transport", dayIndex: number, inventoryId: string) => {
    const inventory =
      type === "activity" ? activityInventory : transportInventory.filter((item) => item.rateBasis !== "daily");
    const item = findInventory(inventory, inventoryId);
    if (!item) return;
    const day = (form.getFieldValue(["days", dayIndex]) || {
      day: dayIndex,
      activities: [],
      transports: [],
    }) as BuilderFormValues["days"][number];
    const key = type === "activity" ? "activities" : "transports";
    const currentItems = [...(day[key] || [])] as Array<{
      inventoryId: string;
      quantity: number;
      selectedVehicleType?: string | null;
      startTime?: string | null;
      durationHours?: number | null;
    }>;
    if (currentItems.some((service) => service.inventoryId === inventoryId)) {
      message.info(`This ${type} is already added to day ${dayIndex + 1}`);
      return;
    }
    const nextItem = {
      inventoryId,
      quantity: 1,
      selectedVehicleType: null,
      ...(type === "transport" && transportHasEditableDuration(item)
        ? { durationHours: item.duration || undefined }
        : {}),
    };
    if (type === "activity") activityListOperationsByDayRef.current.get(dayIndex)?.add(nextItem);
    else addPortalTransports([item], dayIndex);
    warnForRouteMismatches([{ inventory: item, dayIndex }]);
  };

  const saveDraft = async (validatedValues: BuilderFormValues, navigateAfterSave = true) => {
    const result = await saveDraftMutation.mutateAsync({
      itineraryId: editingItineraryId,
      leadId: editingItineraryId ? undefined : linkedLeadId,
      guestName: editingItineraryId || linkedLeadId ? undefined : guestName,
      payload: buildPortalItineraryPayload(validatedValues),
    });
    if (markupDirty) {
      try {
        await saveMarkupMutation.mutateAsync({ itineraryId: result.id, markup: draftToMarkup(markupDraft) });
        setMarkupDirty(false);
      } catch {
        message.warning("The quote was saved, but your markup was not. Add it again from the quote page with Edit markup.");
      }
    }
    message.success("Quote saved. You can download it or duplicate it from the pricing card.");
    const emptyDaysOnSave = getEmptyDayNumbers(validatedValues.days);
    if (emptyDaysOnSave.length > 0) {
      message.warning(
        `${formatEmptyDays(emptyDaysOnSave)} ${emptyDaysOnSave.length === 1 ? "is" : "are"} empty. Use Edit days in the daily plan to adjust the route.`,
      );
    }
    setIsDirty(false);
    // Stay in the editor so the saved quote can be downloaded or duplicated; a new quote moves to its own edit URL.
    if (navigateAfterSave && !editingItineraryId && result.id) {
      allowNavigationRef.current = true;
      navigate(portalPaths.editItinerary(result.id), { replace: true });
    }
    return result;
  };

  const downloadSavedQuote = async (options: DownloadItineraryOptions) => {
    if (!editingItineraryId || downloading) return;
    try {
      setDownloading(true);
      setDownloadModalOpen(false);
      const response = await downloadPortalItinerary(editingItineraryId, options);
      const disposition = String(response.headers["content-disposition"] || "");
      const reference = String(draftQuery.data?.reference || "quote").replace("#", "");
      const filename =
        disposition.match(/filename="?([^"]+)"?/i)?.[1] ||
        `itinerary-${reference}.${options.format === "docx" ? "docx" : "pdf"}`;
      FileDownload(response.data, filename);
      message.success("Itinerary downloaded");
    } catch (error) {
      message.error(getRequestError(error, "Unable to download the itinerary"));
    } finally {
      setDownloading(false);
    }
  };

  const duplicateSavedQuote = async () => {
    if (!editingItineraryId) return;
    try {
      const copy = await duplicateMutation.mutateAsync(editingItineraryId);
      message.success("Copy created. You are editing the copy.");
      allowNavigationRef.current = true;
      navigate(portalPaths.editItinerary(copy.id));
    } catch (error) {
      message.error(getRequestError(error, "Unable to duplicate the quote"));
    }
  };

  const prepareSubmission = async (validatedValues: BuilderFormValues) => {
    const invalidHotelIndex = validatedValues.hotels.findIndex(
      (hotel) =>
        !hotel.inventoryId || !hotel.stayDates?.[0] || !hotel.stayDates?.[1] || !hotel.roomType || !hotel.mealPlan,
    );
    if (invalidHotelIndex >= 0) {
      setEditingService({ type: "hotel", index: invalidHotelIndex });
      message.error("Complete the hotel details before saving the quote");
      return;
    }
    for (let dayIndex = 0; dayIndex < validatedValues.days.length; dayIndex += 1) {
      const day = validatedValues.days[dayIndex];
      const invalidActivityIndex = day.activities.findIndex((activity) => {
        const item = findInventory(activityInventory, activity.inventoryId);
        return (
          !activity.inventoryId ||
          !activity.quantity ||
          (activitySupportsVehicle(item) && !activity.selectedVehicleType)
        );
      });
      if (invalidActivityIndex >= 0) {
        setEditingService({ type: "activity", dayIndex, index: invalidActivityIndex });
        message.error(`Complete the activity details for day ${dayIndex + 1}`);
        return;
      }
      const invalidTransportIndex = day.transports.findIndex((transport) => {
        const item = findInventory(transportInventory, transport.inventoryId);
        return (
          !transport.inventoryId || !transport.quantity || (item?.rateBasis === "hourly" && !transport.durationHours)
        );
      });
      if (invalidTransportIndex >= 0) {
        setEditingService({ type: "transport", dayIndex, index: invalidTransportIndex });
        message.error(`Complete the transport details for day ${dayIndex + 1}`);
        return;
      }
    }
    const invalidDailyTransportIndex = validatedValues.dailyTransports.findIndex((transport) => {
      const inventory = findInventory(transportInventory, transport.inventoryId);
      return (
        !transport.inventoryId ||
        inventory?.rateBasis !== "daily" ||
        !transport.quantity ||
        !transport.startDate ||
        !transport.endDate
      );
    });
    if (invalidDailyTransportIndex >= 0) {
      setEditingService({ type: "fullDayTransport", index: invalidDailyTransportIndex });
      message.error("Complete the Full Day transport details before saving the quote");
      return;
    }
    setSubmissionError(null);
    if (actionIntent === "save") {
      try {
        await saveDraft(validatedValues);
      } catch (error) {
        setSubmissionError(getRequestError(error, "Unable to save the itinerary"));
      }
      return;
    }
    setConfirmationValues(validatedValues);
  };

  const submit = async () => {
    if (!confirmationValues || submitMutation.isPending) return;
    try {
      setSubmissionError(null);
      const draft = await saveDraft(confirmationValues, false);
      const result = await submitMutation.mutateAsync(draft.id);
      setConfirmationValues(null);
      message.success(`Quote confirmed with ${organisationName}.`);
      allowNavigationRef.current = true;
      navigate(draft.leadId ? portalPaths.request(draft.leadId) : portalPaths.itinerary(result.id), { replace: true });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await Promise.all([hotelsQuery.refetch(), activitiesQuery.refetch(), transportsQuery.refetch()]);
      }
      setSubmissionError(getRequestError(error, "Unable to confirm the quote"));
    }
  };

  const inventoryError = hotelsQuery.isError || activitiesQuery.isError || transportsQuery.isError;
  const editingHotel =
    editingService?.type === "hotel"
      ? (form.getFieldValue(["hotels", editingService.index]) as BuilderFormValues["hotels"][number] | undefined)
      : undefined;
  const editingActivity =
    editingService?.type === "activity"
      ? (form.getFieldValue(["days", editingService.dayIndex, "activities", editingService.index]) as
          | BuilderFormValues["days"][number]["activities"][number]
          | undefined)
      : undefined;
  const editingTransport =
    editingService?.type === "transport"
      ? (form.getFieldValue(["days", editingService.dayIndex, "transports", editingService.index]) as
          | BuilderFormValues["days"][number]["transports"][number]
          | undefined)
      : undefined;
  const editingFullDayTransport =
    editingService?.type === "fullDayTransport"
      ? (form.getFieldValue(["dailyTransports", editingService.index]) as
          | BuilderFormValues["dailyTransports"][number]
          | undefined)
      : undefined;
  const editingInventory =
    editingService?.type === "hotel"
      ? findInventory(hotelInventory, editingHotel?.inventoryId)
      : editingService?.type === "activity"
        ? findInventory(activityInventory, editingActivity?.inventoryId)
        : editingService?.type === "transport"
          ? findInventory(transportInventory, editingTransport?.inventoryId)
          : editingService?.type === "fullDayTransport"
            ? findInventory(transportInventory, editingFullDayTransport?.inventoryId)
            : undefined;

  return (
    <PortalPageShell>
      <div className="space-y-3">
        {/* The route and template steps are full pages; the builder stays mounted so its form state is kept. */}
        <div className={isFlowPageOpen ? "hidden" : "space-y-3"}>
        <PortalPageHeader
          title={editingItineraryId ? "Edit saved quote" : linkedLeadId ? "New quote option" : "Create trip"}
          description={`Planning for ${actor.agency?.name || "your agency"} with ${organisationName}'s curated inventory.`}
          meta={
            editingItineraryId
              ? draftQuery.data?.reference || "Saved quote"
              : linkedRequestQuery.data?.reference || "New trip"
          }
          actions={
            hasConfirmedTripDetails ? (
              <Button icon={<FileTextIcon className="h-4 w-4" />} onClick={openQuoteRecommendations}>
                AI recommendations
              </Button>
            ) : undefined
          }
        />

        {hasConfirmedTripDetails && inventoryError ? (
          <Alert
            type="error"
            showIcon
            message="Some inventory could not be loaded"
            description="Refresh the page before building the itinerary."
          />
        ) : null}

        {draftQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message="Unable to load this saved quote"
            description="It may already have been confirmed."
          />
        ) : null}

        {linkedLeadId && linkedRequestQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message="Unable to load the trip"
            description="Return to Trips and choose an editable trip before creating another quote."
          />
        ) : null}

        {hasConfirmedTripDetails && routeConflictWarnings.length ? (
          <div>
            <Alert
              type="warning"
              showIcon
              message={`${routeConflictWarnings.length} item${routeConflictWarnings.length === 1 ? "" : "s"} outside the planned route`}
              description="The items were kept. Route stops can only be changed from Plan route stops."
            />
          </div>
        ) : null}

        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={prepareSubmission}
          onValuesChange={() => setIsDirty(true)}
          requiredMark="optional"
        >
          <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4"><ChildPassengersForm form={form} onOperations={(operations) => { childOperationsRef.current = operations; }} /></div>
          <div className="grid gap-4 lg:grid-cols-12">
            <div
              className={`flex min-w-0 flex-col gap-4 ${hasConfirmedTripDetails ? "lg:col-span-9" : "lg:col-span-12"}`}
            >
              <Anchor
                offsetTop={sectionNavOffset}
                targetOffset={sectionNavOffset + 64}
                bounds={8}
                affix
                direction="horizontal"
                className={`${hasConfirmedTripDetails ? "" : "hidden"} order-2 sticky z-20 overflow-x-auto rounded-xl border border-gray-200 bg-white !py-0 shadow-[0_1px_2px_rgba(19,27,42,0.04)] [&_.ant-anchor]:!min-w-max [&_.ant-anchor-ink-ball]:!border-(--brand-primary) [&_.ant-anchor-link-title-active]:!text-[#131B2A]`}
                style={{ marginBlockStart: 0 }}
                items={[
                  {
                    key: "trip-details",
                    href: "#trip-details",
                    title: (
                      <span className="flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-[#FBFBFB] hover:text-[#131B2A]">
                        <CalendarDaysIcon className="mr-2 h-4 w-4 text-(--brand-primary)" /> Trip details
                      </span>
                    ),
                  },
                  {
                    key: "hotels",
                    href: "#hotels",
                    title: (
                      <span className="flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-[#FBFBFB] hover:text-[#131B2A]">
                        <BedDoubleIcon className="mr-2 h-4 w-4 text-(--brand-primary)" /> Hotels
                      </span>
                    ),
                  },
                  {
                    key: "daily-plan",
                    href: "#daily-plan",
                    title: (
                      <span className="flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-[#FBFBFB] hover:text-[#131B2A]">
                        <BusIcon className="mr-2 h-4 w-4 text-(--brand-primary)" /> Daily plan
                      </span>
                    ),
                  },
                  {
                    key: "terms",
                    href: "#terms",
                    title: (
                      <span className="flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-[#FBFBFB] hover:text-[#131B2A]">
                        <MessageSquareMoreIcon className="mr-2 h-4 w-4 text-(--brand-primary)" /> Terms & conditions
                      </span>
                    ),
                  },
                ]}
              />
              <section
                id="daily-plan"
                className={`${hasConfirmedTripDetails ? "" : "hidden"} order-4 scroll-mt-32 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]`}
              >
                <div className="flex min-h-14 items-center gap-3 border-b border-gray-200 px-4 py-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-(--brand-primary)/25 bg-(--brand-primary)/10">
                    <TentTreeIcon className="h-4 w-4 text-(--brand-primary)" />
                  </span>
                  <div>
                    <h2 className="text-sm font-semibold text-[#131B2A]">Daily plan</h2>
                    <p className="text-xs text-gray-500">Activities and ground transport in one timeline</p>
                  </div>
                </div>
                {emptyDayNumbers.length > 0 ? (
                  <Alert
                    className="!rounded-none !border-x-0 !border-t-0"
                    type="warning"
                    showIcon
                    message={`${formatEmptyDays(emptyDayNumbers)} ${emptyDayNumbers.length === 1 ? "is" : "are"} empty`}
                    description="Nothing is planned for these days, so they will print blank in the itinerary. Add services, or change the route to shorten the trip."
                    action={
                      <Button size="small" icon={<RouteIcon className="h-3.5 w-3.5" />} onClick={openRoutePlanner}>
                        Edit days
                      </Button>
                    }
                  />
                ) : null}
                {activitiesQuery.isLoading || transportsQuery.isLoading ? (
                  <div className="p-4">
                    <InventoryLoading />
                  </div>
                ) : (
                  <Form.List name="days">
                    {(dayFields) => (
                      <div className="divide-y divide-gray-200">
                        {dayFields.map((dayField, dayIndex) => (
                          <div key={dayField.key} className="grid grid-cols-1 lg:grid-cols-12">
                            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 lg:col-span-2 lg:border-b-0 lg:border-r">
                              <p className="inline-block rounded-md bg-(--brand-secondary) px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.11em] text-white">
                                Day {dayIndex + 1}
                              </p>
                              <p className="mt-1 text-sm font-semibold text-[#131B2A]">
                                {dayjs(startDate || dayjs())
                                  .add(dayIndex, "day")
                                  .format("DD MMM")}
                              </p>
                              <p className="mt-2 text-[11px] text-gray-500">
                                {dayjs(startDate || dayjs())
                                  .add(dayIndex, "day")
                                  .format("dddd")}
                              </p>
                            </div>
                            <div className="min-w-0 lg:col-span-10">
                              <Form.List name={[dayField.name, "transports"]}>
                                {(fields, operations) => {
                                  const { remove } = operations;
                                  transportListOperationsByDayRef.current.set(dayIndex, operations);
                                  return (
                                    <div className="divide-y divide-gray-100">
                                      {fields.map((field) => {
                                        const service = form.getFieldValue([
                                          "days",
                                          dayField.name,
                                          "transports",
                                          field.name,
                                        ]) as BuilderFormValues["days"][number]["transports"][number];
                                        const selected = findInventory(
                                          transportInventory.filter((item) => item.rateBasis !== "daily"),
                                          service?.inventoryId,
                                        );
                                        return (
                                          <div
                                            key={field.key}
                                            className="flex min-h-[50px] items-center gap-3 px-3 py-2 transition-colors hover:bg-gray-50"
                                          >
                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                                              <BusIcon size={14} />
                                            </span>
                                            <button
                                              type="button"
                                              className="min-w-0 flex-1 text-left"
                                              onClick={() =>
                                                setEditingService({ type: "transport", dayIndex, index: field.name })
                                              }
                                            >
                                              <div className="flex flex-wrap items-center gap-2">
                                                <span className="truncate text-sm font-semibold text-[#131B2A]">
                                                  {selected?.name || "Transport details required"}
                                                </span>
                                                <Tag
                                                  bordered={false}
                                                  className="!m-0 !bg-blue-50 !text-[10px] !font-semibold !text-blue-700"
                                                >
                                                  TRANSPORT
                                                </Tag>
                                              </div>
                                              <p className="mt-0.5 truncate text-[11px] text-gray-500">
                                                {[
                                                  selected?.city,
                                                  service?.startTime,
                                                  service?.durationHours
                                                    ? `${service.durationHours} hr`
                                                    : selected?.duration
                                                      ? `${selected.duration} hr`
                                                      : null,
                                                  parseVehicleValue(service?.selectedVehicleType).vehicleType,
                                                ]
                                                  .filter(Boolean)
                                                  .join(" · ") || "Open details to configure"}
                                              </p>
                                            </button>
                                            {selected?.vehicleOptions?.length ? (
                                              <Form.Item name={[field.name, "selectedVehicleType"]} noStyle>
                                                <Select
                                                  size="small"
                                                  allowClear
                                                  className="w-44 shrink-0"
                                                  placeholder="Vehicle"
                                                  aria-label="Select vehicle"
                                                  options={selected.vehicleOptions.map((vehicle) => ({
                                                    value: vehicleOptionValue(vehicle),
                                                    label: vehicle.pax ? `${vehicle.vehicleType} · ${vehicle.pax} pax` : vehicle.vehicleType,
                                                  }))}
                                                  onChange={(value?: string) => changeTransportVehicle(["days", dayField.name, "transports", field.name], selected, value)}
                                                />
                                              </Form.Item>
                                            ) : null}
                                            <Button
                                              type="text"
                                              size="small"
                                              aria-label="Edit transport"
                                              icon={<PencilIcon size={14} />}
                                              onClick={() =>
                                                setEditingService({ type: "transport", dayIndex, index: field.name })
                                              }
                                            />
                                            <Button
                                              type="text"
                                              size="small"
                                              aria-label="Replace transport"
                                              title="Replace transport"
                                              icon={<RefreshCwIcon size={14} />}
                                              onClick={() =>
                                                setBrowserTarget({
                                                  type: "transport",
                                                  dayIndex,
                                                  replaceIndex: field.name,
                                                })
                                              }
                                            />
                                            <Button
                                              type="text"
                                              size="small"
                                              danger
                                              aria-label="Remove transport"
                                              icon={<Trash2Icon size={14} />}
                                              onClick={() => remove(field.name)}
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                }}
                              </Form.List>
                              <Form.List name={[dayField.name, "activities"]}>
                                {(fields, operations) => {
                                  const { remove } = operations;
                                  activityListOperationsByDayRef.current.set(dayIndex, operations);
                                  return (
                                    <div className="divide-y divide-gray-100">
                                      {fields.map((field) => {
                                        const service = form.getFieldValue([
                                          "days",
                                          dayField.name,
                                          "activities",
                                          field.name,
                                        ]) as BuilderFormValues["days"][number]["activities"][number];
                                        const selected = findInventory(activityInventory, service?.inventoryId);
                                        const linkedTransports =
                                          linkedTransportsQuery.data?.get(service?.inventoryId) || [];
                                        const linkedTransportIds = new Set(
                                          linkedTransports.map((transport) => transport.id),
                                        );
                                        const hasPrivateTransport = (values?.days?.[dayIndex]?.transports || []).some(
                                          (transport) => linkedTransportIds.has(transport.inventoryId),
                                        );
                                        return (
                                          <div
                                            key={field.key}
                                            className="flex flex-wrap min-h-[50px] items-center gap-3 px-3 py-2 transition-colors hover:bg-gray-50"
                                          >
                                            <PortalActivityAgeRates form={form} day={dayField.name} index={field.name} bands={selected?.childAgeBands || []} selection={totalPriceMutation.data?.ageSelections?.find((entry) => entry.day === dayIndex)?.activities[field.name] || draftQuery.data?.itinerary.days.find((entry) => entry.day === dayIndex)?.activities[field.name]} expanded={editingService?.type === "activity" && editingService.dayIndex === dayIndex && editingService.index === field.name} />
                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-(--brand-primary)/10 text-(--brand-primary)">
                                              <TentTreeIcon size={14} />
                                            </span>
                                            <button
                                              type="button"
                                              className="min-w-0 flex-1 text-left"
                                              onClick={() =>
                                                setEditingService({ type: "activity", dayIndex, index: field.name })
                                              }
                                            >
                                              <div className="flex flex-wrap items-center gap-2">
                                                <span className="truncate text-sm font-semibold text-[#131B2A]">
                                                  {selected?.name || "Activity details required"}
                                                </span>
                                                <Tag
                                                  bordered={false}
                                                  className="!m-0 !bg-(--brand-primary)/10 !text-[10px] !font-semibold !text-(--brand-primary)"
                                                >
                                                  ACTIVITY
                                                </Tag>
                                              </div>
                                              <p className="mt-0.5 truncate text-[11px] text-gray-500">
                                                {[
                                                  selected?.city,
                                                  selected?.duration ? `${selected.duration} hr` : null,
                                                  parseVehicleValue(service?.selectedVehicleType).vehicleType,
                                                ]
                                                  .filter(Boolean)
                                                  .join(" · ") || "Open details to configure"}
                                              </p>
                                            </button>
                                            {linkedTransports.length > 0 ? (
                                              <div className="flex shrink-0 flex-col items-end gap-0.5">
                                                <Segmented
                                                  size="small"
                                                  aria-label={`Service mode for ${selected?.name || "activity"}`}
                                                  value={hasPrivateTransport ? "private" : "sic"}
                                                  options={[
                                                    { label: "SIC", value: "sic" },
                                                    { label: "Private", value: "private" },
                                                  ]}
                                                  onChange={(mode) =>
                                                    setActivityServiceMode(
                                                      dayIndex,
                                                      linkedTransports,
                                                      mode as "sic" | "private",
                                                    )
                                                  }
                                                />
                                                {hasPrivateTransport ? (
                                                  <span className="text-[10px] text-gray-500">
                                                    Vehicle and entry tickets for {partySize} guest
                                                    {partySize === 1 ? "" : "s"}
                                                  </span>
                                                ) : null}
                                              </div>
                                            ) : null}
                                            <Button
                                              type="text"
                                              size="small"
                                              aria-label="Edit activity"
                                              icon={<PencilIcon size={14} />}
                                              onClick={() =>
                                                setEditingService({ type: "activity", dayIndex, index: field.name })
                                              }
                                            />
                                            <Button
                                              type="text"
                                              size="small"
                                              aria-label="Replace activity"
                                              title="Replace activity"
                                              icon={<RefreshCwIcon size={14} />}
                                              onClick={() =>
                                                setBrowserTarget({
                                                  type: "activity",
                                                  dayIndex,
                                                  replaceIndex: field.name,
                                                })
                                              }
                                            />
                                            <Button
                                              type="text"
                                              size="small"
                                              danger
                                              aria-label="Remove activity"
                                              icon={<Trash2Icon size={14} />}
                                              onClick={() => remove(field.name)}
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                }}
                              </Form.List>
                              <div className="flex flex-col gap-2 border-t border-gray-200 bg-[#FBFBFB] p-3 sm:flex-row sm:items-center">
                                <Select
                                  showSearch
                                  value={undefined}
                                  filterOption={filterInventoryOption}
                                  placeholder="Search and add an activity"
                                  options={inventoryOptions(activityInventory)}
                                  onChange={(id: string) => quickAddDayService("activity", dayIndex, id)}
                                  className="min-w-0 flex-1"
                                />
                                <div className="flex shrink-0 gap-2">
                                  <Button
                                    size="small"
                                    aria-label={`Browse activities for day ${dayIndex + 1}`}
                                    icon={<PlusIcon size={13} />}
                                    onClick={() => setBrowserTarget({ type: "activity", dayIndex })}
                                  >
                                    Activity
                                  </Button>
                                  <Button
                                    size="small"
                                    aria-label={`Browse transfers for day ${dayIndex + 1}`}
                                    icon={<PlusIcon size={13} />}
                                    onClick={() => setBrowserTarget({ type: "transport", dayIndex })}
                                  >
                                    Transfer
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Form.List>
                )}
              </section>

              <section
                id="daily-transports"
                className={`${hasConfirmedTripDetails ? "" : "hidden"} order-5 scroll-mt-32 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]`}
              >
                <div className="flex min-h-14 items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                      <BusIcon size={14} />
                    </span>
                    <div>
                      <h2 className="text-sm font-semibold text-[#131B2A]">Full Day Transports</h2>
                      <p className="text-xs text-gray-500">Cab services covering a selected date range</p>
                    </div>
                  </div>
                  <Button
                    size="small"
                    icon={<PlusIcon size={14} />}
                    onClick={() => setBrowserTarget({ type: "transport", daily: true })}
                  >
                    Browse Full Day transports
                  </Button>
                </div>
                <Form.List name="dailyTransports">
                  {(fields, { add, remove, move }) => (
                    <>
                      <div className="border-b border-gray-100 bg-gray-50/60 px-4 py-3">
                        <Select
                          showSearch
                          allowClear
                          value={undefined}
                          placeholder="Quick search Full Day transport inventory"
                          filterOption={filterInventoryOption}
                          options={inventoryOptions(
                            transportInventory.filter(
                              (item) =>
                                item.rateBasis === "daily" &&
                                item.serviceType === "cab" &&
                                fields.every(
                                  (field) =>
                                    form.getFieldValue(["dailyTransports", field.name, "inventoryId"]) !== item.id,
                                ),
                            ),
                          )}
                          onSelect={(inventoryId) => {
                            const item = findInventory(transportInventory, inventoryId);
                            if (!item) return;
                            const tripStart = dayjs(startDate);
                            const tripEnd = tripStart.add(Number(nights), "day");
                            add({
                              inventoryId: item.id,
                              quantity: 1,
                              selectedVehicleType: firstVehicleValue(item.vehicleOptions) || null,
                              serviceDates: [tripStart, tripEnd],
                              startDate: tripStart.format("YYYY-MM-DD"),
                              endDate: tripEnd.format("YYYY-MM-DD"),
                            });
                          }}
                          className="w-full"
                        />
                      </div>
                      {fields.length ? (
                        <div className="divide-y divide-gray-100">
                          {fields.map((field, index) => {
                            const selection = form.getFieldValue([
                              "dailyTransports",
                              field.name,
                            ]) as BuilderFormValues["dailyTransports"][number];
                            const selected = findInventory(transportInventory, selection?.inventoryId);
                            const start = selection?.startDate
                              ? dayjs(selection.startDate)
                              : selection?.serviceDates?.[0];
                            const end = selection?.endDate ? dayjs(selection.endDate) : selection?.serviceDates?.[1];
                            const durationDays = start?.isValid() && end?.isValid() ? end.diff(start, "day") + 1 : null;
                            return (
                              <div
                                key={field.key}
                                className="flex min-h-[64px] items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50"
                              >
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                                  <BusIcon size={15} />
                                </span>
                                <button
                                  type="button"
                                  className="min-w-0 flex-1 text-left"
                                  onClick={() => setEditingService({ type: "fullDayTransport", index: field.name })}
                                >
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="truncate text-sm font-semibold text-[#131B2A]">
                                      {selected?.name || "Full Day transport details required"}
                                    </span>
                                    <Tag
                                      bordered={false}
                                      className="!m-0 !bg-blue-50 !text-[10px] !font-semibold !text-blue-700"
                                    >
                                      FULL DAY
                                    </Tag>
                                  </div>
                                  <p className="mt-0.5 truncate text-[11px] text-gray-500">
                                    {[
                                      selected?.city,
                                      start?.isValid() && end?.isValid()
                                        ? `${start.format("DD MMM")} - ${end.format("DD MMM")}`
                                        : null,
                                      durationDays ? `${durationDays} day${durationDays === 1 ? "" : "s"}` : null,
                                      parseVehicleValue(selection?.selectedVehicleType).vehicleType,
                                      selection?.quantity ? `Qty ${selection.quantity}` : null,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ") || "Open details to configure"}
                                  </p>
                                </button>
                                {selected?.vehicleOptions?.length ? (
                                  <Form.Item name={[field.name, "selectedVehicleType"]} noStyle>
                                    <Select
                                      size="small"
                                      allowClear
                                      className="w-44 shrink-0"
                                      placeholder="Vehicle"
                                      aria-label="Select vehicle"
                                      options={selected.vehicleOptions.map((vehicle) => ({
                                        value: vehicleOptionValue(vehicle),
                                        label: vehicle.pax ? `${vehicle.vehicleType} · ${vehicle.pax} pax` : vehicle.vehicleType,
                                      }))}
                                      onChange={(value?: string) => changeTransportVehicle(["dailyTransports", field.name], selected, value)}
                                    />
                                  </Form.Item>
                                ) : null}
                                <Button
                                  type="text"
                                  size="small"
                                  aria-label="Edit Full Day transport"
                                  icon={<PencilIcon size={14} />}
                                  onClick={() => setEditingService({ type: "fullDayTransport", index: field.name })}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  disabled={index === 0}
                                  aria-label="Move Full Day transport up"
                                  icon={<ArrowUpIcon size={14} />}
                                  onClick={() => move(index, index - 1)}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  disabled={index === fields.length - 1}
                                  aria-label="Move Full Day transport down"
                                  icon={<ArrowDownIcon size={14} />}
                                  onClick={() => move(index, index + 1)}
                                />
                                <Button
                                  danger
                                  type="text"
                                  size="small"
                                  aria-label="Remove Full Day transport"
                                  icon={<Trash2Icon size={14} />}
                                  onClick={() => remove(field.name)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="No Full Day transports added"
                          className="py-6"
                        />
                      )}
                    </>
                  )}
                </Form.List>
              </section>

              <section
                id="trip-details"
                aria-label={hasConfirmedTripDetails ? "Trip details summary" : "Trip details"}
                className="order-1 scroll-mt-32 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]"
              >
                <div className="grid grid-cols-1 divide-y divide-gray-200 lg:grid-cols-[minmax(0,1fr)_auto] lg:divide-x lg:divide-y-0">
                  {hasConfirmedTripDetails ? (
                    <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-(--brand-primary) text-[var(--brand-primary-foreground)]">
                          <RouteIcon className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">Route</p>
                          <p className="max-w-[34rem] truncate text-sm font-semibold text-[#131B2A]">
                            {hasCompleteCityRoute
                              ? routeStops.map((stop) => stop.city || stop.country).join(" · ")
                              : destinations.join(" · ")}
                          </p>
                          <p className="mt-0.5 text-[10px] text-gray-400">
                            {hasCompleteCityRoute
                              ? `${routeStops.length} stop${routeStops.length === 1 ? "" : "s"}`
                              : "Destination-level route"}
                          </p>
                        </div>
                      </div>
                      <span className="hidden h-8 w-px bg-gray-200 sm:block" aria-hidden="true" />
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                          Travel dates
                        </p>
                        <p className="text-sm font-medium tabular-nums text-[#131B2A]">
                          {dayjs(startDate).format("DD MMM")}–
                          {dayjs(startDate).add(Number(nights), "day").format("DD MMM YYYY")} · {Number(nights)} nights
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <UsersRoundIcon className="h-4 w-4 text-(--brand-primary)" aria-hidden="true" />
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">Guests</p>
                          <p className="text-sm font-medium text-[#131B2A]">
                            {Number(values?.adults || 0)} adults
                            {values?.cwb ? ` · ${values.cwb} CWB` : ""}
                            {values?.cnb ? ` · ${values.cnb} CNB` : ""}
                            {values?.infant ? ` · ${values.infant} infants` : ""}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                          Selling currency
                        </p>
                        <p className="text-sm font-medium text-[#131B2A]">{sellingCurrency}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="min-h-14" aria-hidden="true" />
                  )}
                  <div className="flex flex-wrap items-center justify-end gap-1 px-3 py-2">
                    <Button type="text" icon={<PencilIcon className="h-3.5 w-3.5" />} onClick={openTripDetailsEditor}>
                      Trip details
                    </Button>
                    {hasConfirmedTripDetails ? (
                      <Button
                        type="text"
                        icon={<RouteIcon className="h-3.5 w-3.5" />}
                        className="!font-semibold !text-(--brand-primary)"
                        onClick={openRoutePlanner}
                      >
                        {hasCompleteCityRoute ? "Edit route" : "Add stops"}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </section>

              <Modal
                open={isTripDetailsModalOpen}
                onCancel={cancelTripDetails}
                title={
                  <div className="flex items-center gap-3 pr-8">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-(--brand-primary) text-[var(--brand-primary-foreground)]">
                      <RouteIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold leading-5 text-[#131B2A]">
                        {hasConfirmedTripDetails ? "Edit trip details" : "Trip details"}
                      </h2>
                      <p className="mt-0.5 text-xs font-normal leading-4 text-slate-500">
                        Set the destination, dates and traveller mix before building the itinerary.
                      </p>
                    </div>
                  </div>
                }
                width={760}
                style={{ top: 24 }}
                footer={
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                    <Button onClick={cancelTripDetails}>
                      {hasConfirmedTripDetails ? "Cancel" : "Cancel itinerary"}
                    </Button>
                    <Button
                      type="primary"
                      className="!border-(--brand-secondary) !bg-(--brand-secondary) !px-4"
                      onClick={confirmTripDetails}
                    >
                      {hasConfirmedTripDetails ? "Save details" : "Plan route stops"}
                    </Button>
                  </div>
                }
                classNames={{
                  header: "mb-0! !border-b !border-slate-200 !px-5 !py-4",
                  body: "max-h-[calc(100dvh-190px)] overflow-y-auto !px-5 !py-4",
                  footer: "!mt-0 !border-t !border-slate-200 !px-5 !py-3",
                  content: "!overflow-hidden !rounded-xl !p-0",
                }}
                forceRender
              >
                <div className="[&_.ant-form-item]:mb-0! [&_.ant-form-item-label]:!pb-1.5 [&_.ant-form-item-label>label]:!h-auto [&_.ant-form-item-label>label]:!text-xs [&_.ant-form-item-label>label]:!font-semibold [&_.ant-form-item-label>label]:!text-slate-700">
                  <section aria-labelledby="trip-setup-heading">
                    <div className="mb-3 flex items-start gap-2.5">
                      <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-(--brand-primary)" aria-hidden="true" />
                      <div>
                        <h3 id="trip-setup-heading" className="text-sm font-semibold text-[#131B2A]">
                          Trip
                        </h3>
                        <p className="text-xs leading-4 text-slate-500">
                          Choose the countries, departure date and duration.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-3">
                      <Form.Item
                        className="sm:col-span-3"
                        label="Destinations"
                        name="destinations"
                        rules={[{ required: true, message: "Add at least one destination" }]}
                      >
                        <Select
                          mode="multiple"
                          size="large"
                          showSearch
                          open={isDestinationDropdownOpen}
                          onOpenChange={setIsDestinationDropdownOpen}
                          onSelect={(destination) => {
                            const selectedDestinations = (form.getFieldValue("destinations") || []) as string[];
                            if (new Set([...selectedDestinations, destination]).size === 1) {
                              setIsDestinationDropdownOpen(false);
                            }
                          }}
                          optionFilterProp="label"
                          loading={countriesQuery.isLoading}
                          placeholder="Select destination countries"
                          maxCount={12}
                          maxTagCount="responsive"
                          options={(countriesQuery.data || []).map((country) => ({ value: country, label: country }))}
                          notFoundContent="No destination found"
                        />
                      </Form.Item>
                      <Form.Item
                        label="Start date"
                        name="startDate"
                        rules={[{ required: true, message: "Choose a start date" }]}
                      >
                        <DatePicker
                          className="w-full"
                          size="large"
                          format="DD MMM YYYY"
                          disabledDate={(date) => date.isBefore(dayjs().startOf("day"))}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Nights"
                        name="nights"
                        rules={[{ required: true, message: "Enter the trip duration" }]}
                      >
                        <InputNumber
                          className="w-full"
                          size="large"
                          min={1}
                          max={90}
                          precision={0}
                          addonAfter={`${Number(nights) + 1} days`}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Selling currency"
                        name="conversionCurrency"
                        // With a single currency there is nothing to choose, so the selector is hidden (the field stays registered).
                        hidden={sellingCurrencyOptions.length < 2}
                        rules={[{ required: true, message: "Choose a selling currency" }]}
                      >
                        <Select
                          size="large"
                          showSearch
                          optionFilterProp="label"
                          loading={itineraryDefaultsQuery.isLoading}
                          aria-label="Selling currency"
                          options={sellingCurrencyOptions.map((option) => ({ value: option.currency, label: option.currency }))}
                          onChange={(currency) => {
                            const selected = sellingCurrencyOptions.find((option) => option.currency === currency);
                            form.setFieldsValue({
                              conversionCurrency: currency,
                              conversionRate: 1,
                              currencyRate: Number(selected?.rate || 1),
                            });
                          }}
                        />
                      </Form.Item>
                    </div>
                  </section>

                  <section className="mt-5 border-t border-slate-200 pt-4" aria-labelledby="travellers-heading">
                    <div className="mb-3 flex items-start gap-2.5">
                      <UsersRoundIcon className="mt-0.5 h-4 w-4 shrink-0 text-(--brand-primary)" aria-hidden="true" />
                      <div>
                        <h3 id="travellers-heading" className="text-sm font-semibold text-[#131B2A]">
                          Travellers
                        </h3>
                        <p className="text-xs leading-4 text-slate-500">
                          Enter the full guest mix for pricing and rooms.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <Form.Item label="Adults" name="adults" rules={[{ required: true, message: "Enter adults" }]}>
                        <InputNumber className="w-full" size="large" min={1} precision={0} />
                      </Form.Item>
                      <Form.Item label="CWB" name="cwb">
                        <InputNumber className="w-full" size="large" min={0} precision={0} />
                      </Form.Item>
                      <Form.Item label="CNB" name="cnb">
                        <InputNumber className="w-full" size="large" min={0} precision={0} />
                      </Form.Item>
                      <Form.Item label="Infants" name="infant">
                        <InputNumber className="w-full" size="large" min={0} precision={0} />
                      </Form.Item>
                    </div>
                  </section>

                  {!editingItineraryId && !linkedLeadId ? (
                    <section className="mt-5 border-t border-slate-200 pt-4" aria-labelledby="guest-name-heading">
                      <div className="mb-3">
                        <h3 id="guest-name-heading" className="text-sm font-semibold text-[#131B2A]">
                          Reference
                        </h3>
                        <p className="text-xs leading-4 text-slate-500">
                          Optional label to find this request later. Guest phone numbers and contact details are never
                          collected here.
                        </p>
                      </div>
                      <label htmlFor="builder-guest-name" className="mb-1.5 block text-xs font-semibold text-slate-700">
                        Reference name (optional)
                      </label>
                      <Input
                        id="builder-guest-name"
                        size="large"
                        value={guestName}
                        maxLength={128}
                        placeholder="For example, Sharma family, Dec 2026"
                        onChange={(event) => {
                          setGuestName(event.target.value);
                          setIsDirty(true);
                        }}
                      />
                    </section>
                  ) : null}
                  <section className="mt-5 border-t border-slate-200 pt-4" aria-labelledby="request-notes-heading">
                    <h3 id="request-notes-heading" className="mb-3 text-sm font-semibold text-[#131B2A]">
                      Request notes
                    </h3>
                    <Form.Item name="comments">
                      <Input.TextArea
                        rows={3}
                        maxLength={256}
                        showCount
                        placeholder={`Add preferences, occasion details, or instructions for ${organisationName}`}
                      />
                    </Form.Item>
                  </section>
                </div>
              </Modal>

              <div
                aria-label="Trip summary"
                className={`${hasConfirmedTripDetails ? "" : "hidden"} order-3 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-gray-200 bg-gray-200 sm:grid-cols-3 lg:grid-cols-6`}
              >
                {[
                  { label: "Destinations", value: destinations.length || 0 },
                  { label: "Nights", value: Number(nights) || 0 },
                  {
                    label: "Travellers",
                    value: partySize + Number(values?.infant || 0),
                  },
                  { label: "Hotels", value: values?.hotels?.length || 0 },
                  {
                    label: "Activities",
                    value: values?.days?.reduce((sum, day) => sum + (day?.activities?.length || 0), 0) || 0,
                  },
                  {
                    label: "Transfers",
                    value:
                      (values?.days?.reduce((sum, day) => sum + (day?.transports?.length || 0), 0) || 0) +
                      (values?.dailyTransports?.length || 0),
                  },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400">{stat.label}</p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-[#131B2A]">{stat.value}</p>
                  </div>
                ))}
              </div>
              <section
                id="hotels"
                className={`${hasConfirmedTripDetails ? "" : "hidden"} order-3 scroll-mt-32 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]`}
              >
                <div className="flex min-h-14 flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-(--brand-primary)/25 bg-(--brand-primary)/10">
                      <BedDoubleIcon className="h-4 w-4 text-(--brand-primary)" />
                    </span>
                    <div>
                      <h2 className="text-sm font-semibold text-[#131B2A]">Hotels</h2>
                      <p className="text-xs text-gray-500">{values?.hotels?.length || 0} accommodation stays</p>
                    </div>
                  </div>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusIcon size={14} />}
                    onClick={() => setBrowserTarget({ type: "hotel" })}
                  >
                    Add hotel
                  </Button>
                </div>
                {hotelsQuery.isLoading ? (
                  <div className="p-4">
                    <InventoryLoading />
                  </div>
                ) : (
                  <Form.List name="hotels">
                    {(fields, { remove }) => (
                      <>
                        <div className="hidden grid-cols-12 gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.11em] text-gray-500 lg:grid">
                          <span className="col-span-2">City</span>
                          <span className="col-span-2">Stay</span>
                          <span className="col-span-3">Hotel</span>
                          <span className="col-span-3">Room & meal</span>
                          <span className="col-span-2 text-right">Actions</span>
                        </div>
                        {!fields.length ? (
                          <div className="flex min-h-24 flex-col items-center justify-center gap-1 px-4 py-6 text-center">
                            <p className="text-sm font-medium text-gray-700">No hotels added yet</p>
                            <p className="text-xs text-gray-500">Use quick search or browse the available inventory.</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {fields.map((field) => {
                              const hotel = form.getFieldValue([
                                "hotels",
                                field.name,
                              ]) as BuilderFormValues["hotels"][number];
                              const selected = findInventory(hotelInventory, hotel?.inventoryId);
                              const stayNights =
                                hotel?.stayDates?.[0] && hotel?.stayDates?.[1]
                                  ? Math.max(0, dayjs(hotel.stayDates[1]).diff(dayjs(hotel.stayDates[0]), "day"))
                                  : 0;
                              return (
                                <div
                                  key={field.key}
                                  className="grid min-h-[58px] grid-cols-12 items-center gap-3 px-4 py-2 transition-colors hover:bg-gray-50"
                                >
                                  <div className="col-span-12 hidden lg:col-span-2 lg:block">
                                    <span className="inline-flex rounded bg-(--brand-primary)/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-(--brand-primary)">
                                      {selected?.city || "Unassigned"}
                                    </span>
                                  </div>
                                  <div className="hidden items-center gap-2 text-xs text-gray-600 lg:col-span-2 lg:flex">
                                    <CalendarDaysIcon className="h-3.5 w-3.5 text-gray-400" />
                                    <span>
                                      {hotel?.stayDates?.[0] ? dayjs(hotel.stayDates[0]).format("DD MMM") : "Set dates"}
                                      {hotel?.stayDates?.[1] ? ` – ${dayjs(hotel.stayDates[1]).format("DD MMM")}` : ""}
                                      {stayNights ? ` · ${stayNights}N` : ""}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setEditingService({ type: "hotel", index: field.name })}
                                    className="col-span-10 min-w-0 text-left lg:col-span-3"
                                  >
                                    <div className="truncate text-sm font-semibold text-[#131B2A]">
                                      {selected?.name || "Hotel details required"}
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-gray-500 lg:hidden">
                                      {selected?.city ? `${selected.city} · ` : ""}
                                      {hotel?.stayDates?.[0] ? dayjs(hotel.stayDates[0]).format("DD MMM") : "Set dates"}
                                      {stayNights ? ` · ${stayNights}N` : ""}
                                    </div>
                                  </button>
                                  <div className="hidden min-w-0 text-xs text-gray-600 lg:col-span-3 lg:block">
                                    <div className="truncate">{hotel?.roomType || "Room not set"}</div>
                                    <div className="truncate text-[11px] text-gray-400">
                                      {hotel?.mealPlan || "Meal plan not set"}
                                    </div>
                                  </div>
                                  <div className="col-span-2 flex justify-end gap-1 lg:col-span-2">
                                    <Button
                                      type="text"
                                      size="small"
                                      aria-label="Edit hotel"
                                      icon={<PencilIcon size={14} />}
                                      onClick={() => setEditingService({ type: "hotel", index: field.name })}
                                    />
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      aria-label="Remove hotel"
                                      icon={<Trash2Icon size={14} />}
                                      onClick={() => remove(field.name)}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="flex flex-col gap-2 border-t border-gray-200 bg-[#FBFBFB] p-3 sm:flex-row">
                          <Select
                            showSearch
                            value={undefined}
                            filterOption={filterInventoryOption}
                            placeholder="Quick add hotel"
                            options={inventoryOptions(hotelInventory)}
                            onChange={quickAddHotel}
                            className="min-w-0 flex-1"
                          />
                          <Button
                            icon={<LayoutGridIcon size={14} />}
                            onClick={() => setBrowserTarget({ type: "hotel" })}
                          >
                            Browse
                          </Button>
                        </div>
                      </>
                    )}
                  </Form.List>
                )}
              </section>

              <section className="hidden rounded-lg border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
                <SectionHeading
                  icon={MapPinIcon}
                  title="Day plan"
                  description={`Add ${organisationName}'s activities and transport to each day.`}
                />
                {activitiesQuery.isLoading || transportsQuery.isLoading ? (
                  <InventoryLoading />
                ) : (
                  <Form.List name="days">
                    {(dayFields) => (
                      <div className="space-y-3">
                        {dayFields.map((dayField, dayIndex) => (
                          <div key={dayField.key} className="rounded-xl border border-gray-200 bg-[#FBFBFB] p-4">
                            <div className="mb-3 flex items-center gap-3">
                              <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-semibold">
                                {dayIndex + 1}
                              </span>
                              <div>
                                <h3 className="text-sm font-semibold">Day {dayIndex + 1}</h3>
                                <p className="text-xs text-gray-500">
                                  {dayjs(startDate || dayjs())
                                    .add(dayIndex, "day")
                                    .format("ddd, DD MMM YYYY")}
                                </p>
                              </div>
                            </div>
                            <Form.Item name={[dayField.name, "day"]} hidden>
                              <InputNumber />
                            </Form.Item>
                            <div className="grid gap-3">
                              <div className="order-2 rounded-lg border border-gray-200 bg-white p-3">
                                <div className="mb-3 flex items-center gap-2">
                                  <TentTreeIcon size={15} className="text-(--brand-primary)" />
                                  <span className="text-xs font-semibold">Activities</span>
                                </div>
                                <Form.List name={[dayField.name, "activities"]}>
                                  {(fields, operations) => {
                                    const { add, remove } = operations;
                                    activityListOperationsByDayRef.current.set(dayIndex, operations);
                                    return (
                                      <div className="space-y-2">
                                        {fields.map((field) => {
                                          const id = form.getFieldValue([
                                            "days",
                                            dayField.name,
                                            "activities",
                                            field.name,
                                            "inventoryId",
                                          ]);
                                          const selected = findInventory(activityInventory, id);
                                          return (
                                            <div
                                              key={field.key}
                                              className="rounded-lg border border-gray-200 bg-[#FBFBFB] p-3"
                                            >
                                              <div className="flex items-start justify-between gap-3">
                                                <InventoryProductHeader item={selected} fallback="Select activity" />
                                                <Button
                                                  type="text"
                                                  danger
                                                  aria-label="Remove activity"
                                                  icon={<Trash2Icon size={14} />}
                                                  onClick={() => remove(field.name)}
                                                />
                                              </div>
                                              <div className="mt-3">
                                                <Form.Item
                                                  className="mb-0! min-w-0 flex-1"
                                                  name={[field.name, "inventoryId"]}
                                                  rules={[{ required: true, message: "Select an activity" }]}
                                                >
                                                  <Select
                                                    showSearch
                                                    filterOption={filterInventoryOption}
                                                    placeholder="Quick search activities"
                                                    options={inventoryOptions(activityInventory)}
                                                    onChange={() => {
                                                      form.setFieldValue(
                                                        ["days", dayField.name, "activities", field.name, "quantity"],
                                                        1,
                                                      );
                                                      form.setFieldValue(
                                                        [
                                                          "days",
                                                          dayField.name,
                                                          "activities",
                                                          field.name,
                                                          "selectedVehicleType",
                                                        ],
                                                        null,
                                                      );
                                                    }}
                                                  />
                                                </Form.Item>
                                              </div>
                                              {isGroupActivity(selected) ? (
                                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                                  <Form.Item
                                                    className="mb-0!"
                                                    label="Quantity"
                                                    name={[field.name, "quantity"]}
                                                    rules={[{ required: true }]}
                                                  >
                                                    <InputNumber className="w-full" min={1} precision={0} />
                                                  </Form.Item>
                                                  {activitySupportsVehicle(selected) ? (
                                                    <Form.Item
                                                      className="mb-0!"
                                                      label="Vehicle"
                                                      name={[field.name, "selectedVehicleType"]}
                                                      rules={[{ required: true, message: "Select a vehicle" }]}
                                                    >
                                                      <Select
                                                        placeholder="Select vehicle"
                                                        options={(selected?.vehicleOptions || []).map((vehicle) => ({
                                                          value: vehicleOptionValue(vehicle),
                                                          label: vehicle.pax
                                                            ? `${vehicle.vehicleType} · ${vehicle.pax} pax`
                                                            : vehicle.vehicleType,
                                                        }))}
                                                      />
                                                    </Form.Item>
                                                  ) : null}
                                                </div>
                                              ) : (
                                                <Form.Item name={[field.name, "quantity"]} hidden>
                                                  <InputNumber />
                                                </Form.Item>
                                              )}
                                            </div>
                                          );
                                        })}
                                        <div className="flex flex-wrap gap-2">
                                          <Button
                                            size="small"
                                            icon={<PlusIcon size={13} />}
                                            onClick={() => add({ quantity: 1 })}
                                          >
                                            Quick add activity
                                          </Button>
                                          <Button
                                            size="small"
                                            icon={<LayoutGridIcon size={13} />}
                                            onClick={() => setBrowserTarget({ type: "activity", dayIndex })}
                                          >
                                            Browse activities
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  }}
                                </Form.List>
                              </div>
                              <div className="order-1 rounded-lg border border-gray-200 bg-white p-3">
                                <div className="mb-3 flex items-center gap-2">
                                  <BusIcon size={15} className="text-(--brand-primary)" />
                                  <span className="text-xs font-semibold">Transport</span>
                                </div>
                                <Form.List name={[dayField.name, "transports"]}>
                                  {(fields, operations) => {
                                    const { add, remove } = operations;
                                    transportListOperationsByDayRef.current.set(dayIndex, operations);
                                    return (
                                      <div className="space-y-2">
                                        {fields.map((field) => {
                                          const id = form.getFieldValue([
                                            "days",
                                            dayField.name,
                                            "transports",
                                            field.name,
                                            "inventoryId",
                                          ]);
                                          const selected = findInventory(transportInventory, id);
                                          return (
                                            <div
                                              key={field.key}
                                              className="rounded-lg border border-gray-200 bg-[#FBFBFB] p-3"
                                            >
                                              <div className="flex items-start justify-between gap-3">
                                                <InventoryProductHeader item={selected} fallback="Select transport" />
                                                <Button
                                                  type="text"
                                                  danger
                                                  aria-label="Remove transport"
                                                  icon={<Trash2Icon size={14} />}
                                                  onClick={() => remove(field.name)}
                                                />
                                              </div>
                                              <div className="mt-3">
                                                <Form.Item
                                                  className="mb-0! min-w-0 flex-1"
                                                  name={[field.name, "inventoryId"]}
                                                  rules={[{ required: true, message: "Select transport" }]}
                                                >
                                                  <Select
                                                    showSearch
                                                    filterOption={filterInventoryOption}
                                                    placeholder="Quick search transport"
                                                    options={inventoryOptions(
                                                      transportInventory.filter((item) => item.rateBasis !== "daily"),
                                                    )}
                                                    onChange={(transportId) => {
                                                      const item = findInventory(transportInventory, transportId);
                                                      form.setFieldValue(
                                                        ["days", dayField.name, "transports", field.name, "quantity"],
                                                        1,
                                                      );
                                                      form.setFieldValue(
                                                        [
                                                          "days",
                                                          dayField.name,
                                                          "transports",
                                                          field.name,
                                                          "durationHours",
                                                        ],
                                                        transportHasEditableDuration(item)
                                                          ? item?.duration || undefined
                                                          : undefined,
                                                      );
                                                      form.setFieldValue(
                                                        [
                                                          "days",
                                                          dayField.name,
                                                          "transports",
                                                          field.name,
                                                          "selectedVehicleType",
                                                        ],
                                                        null,
                                                      );
                                                      if (item)
                                                        addPortalTransports([item], dayIndex, undefined, field.name);
                                                    }}
                                                  />
                                                </Form.Item>
                                              </div>
                                              {selected ? (
                                                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                                  <Form.Item
                                                    className="mb-0!"
                                                    label="Start time"
                                                    name={[field.name, "startTime"]}
                                                    getValueProps={(value) => ({
                                                      value: value ? dayjs(value, "HH:mm") : null,
                                                    })}
                                                    normalize={(value) => (value ? value.format("HH:mm") : null)}
                                                  >
                                                    <TimePicker
                                                      className="w-full"
                                                      format="HH:mm"
                                                      prefix={<Clock3Icon size={13} />}
                                                    />
                                                  </Form.Item>
                                                  {transportHasEditableDuration(selected) ? (
                                                    <Form.Item
                                                      className="mb-0!"
                                                      label={selected.serviceType === "cab" ? "Hours" : "Duration"}
                                                      name={[field.name, "durationHours"]}
                                                      rules={
                                                        selected.rateBasis === "hourly"
                                                          ? [{ required: true, message: "Enter duration" }]
                                                          : undefined
                                                      }
                                                    >
                                                      <InputNumber
                                                        className="w-full"
                                                        min={0.5}
                                                        step={0.5}
                                                        addonAfter="hrs"
                                                      />
                                                    </Form.Item>
                                                  ) : (
                                                    <div>
                                                      <p className="mb-2 text-sm font-medium text-[#131B2A]">
                                                        Duration
                                                      </p>
                                                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-600">
                                                        {selected.duration
                                                          ? `${selected.duration} hrs`
                                                          : "Fixed service"}
                                                      </div>
                                                    </div>
                                                  )}
                                                  <Form.Item
                                                    className="mb-0!"
                                                    label="Quantity"
                                                    name={[field.name, "quantity"]}
                                                  >
                                                    <InputNumber className="w-full" min={1} precision={0} />
                                                  </Form.Item>
                                                  <Form.Item
                                                    className="mb-0!"
                                                    label="Vehicle"
                                                    name={[field.name, "selectedVehicleType"]}
                                                  >
                                                    <Select
                                                      allowClear
                                                      placeholder="Select vehicle"
                                                      options={(selected.vehicleOptions || []).map((vehicle) => ({
                                                        value: vehicleOptionValue(vehicle),
                                                        label: vehicle.pax
                                                          ? `${vehicle.vehicleType} · ${vehicle.pax} pax`
                                                          : vehicle.vehicleType,
                                                      }))}
                                                    />
                                                  </Form.Item>
                                                </div>
                                              ) : (
                                                <>
                                                  <Form.Item name={[field.name, "quantity"]} hidden>
                                                    <InputNumber />
                                                  </Form.Item>
                                                  <Form.Item name={[field.name, "durationHours"]} hidden>
                                                    <InputNumber />
                                                  </Form.Item>
                                                  <Form.Item name={[field.name, "startTime"]} hidden>
                                                    <Input />
                                                  </Form.Item>
                                                  <Form.Item name={[field.name, "selectedVehicleType"]} hidden>
                                                    <Input />
                                                  </Form.Item>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                        <div className="flex flex-wrap gap-2">
                                          <Button
                                            size="small"
                                            icon={<PlusIcon size={13} />}
                                            onClick={() => add({ quantity: 1 })}
                                          >
                                            Quick add transport
                                          </Button>
                                          <Button
                                            size="small"
                                            icon={<LayoutGridIcon size={13} />}
                                            onClick={() => setBrowserTarget({ type: "transport", dayIndex })}
                                          >
                                            Browse transport
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  }}
                                </Form.List>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Form.List>
                )}
              </section>

              <section
                id="terms"
                className={`${hasConfirmedTripDetails ? "" : "hidden"} order-5 scroll-mt-32 rounded-lg border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgba(19,27,42,0.04)]`}
              >
                <SectionHeading
                  icon={LayoutGridIcon}
                  title="Terms & conditions"
                  description="Updated automatically from the selected inventory and your travel partner’s defaults."
                />
                {inventoryTerms.inclusions.length || inventoryTerms.exclusions.length ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-4">
                      <h3 className="text-sm font-semibold text-emerald-900">Inclusions</h3>
                      {inventoryTerms.inclusions.length ? (
                        <ul className="mt-3 space-y-2 text-sm leading-5 text-emerald-950">
                          {inventoryTerms.inclusions.map((term) => (
                            <li key={term}>• {term}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-2 text-sm text-emerald-800">
                          No inclusions are listed for the selected inventory.
                        </p>
                      )}
                    </div>
                    <div className="rounded-lg border border-red-100 bg-red-50/50 p-4">
                      <h3 className="text-sm font-semibold text-red-900">Exclusions</h3>
                      {inventoryTerms.exclusions.length ? (
                        <ul className="mt-3 space-y-2 text-sm leading-5 text-red-950">
                          {inventoryTerms.exclusions.map((term) => (
                            <li key={term}>• {term}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-2 text-sm text-red-800">
                          No exclusions are listed for the selected inventory.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Select inventory to view its terms and conditions"
                  />
                )}
              </section>
            </div>

            <aside
              aria-label="Quote pricing and actions"
              className={`${hasConfirmedTripDetails ? "" : "hidden"} flex min-w-0 flex-col gap-4 lg:col-span-3 lg:sticky lg:top-20 lg:self-start`}
            >
              {totalPriceMutation.isError ? (
                <Alert
                  type="error"
                  showIcon
                  message="The price could not be calculated"
                  description={getRequestError(totalPriceMutation.error, "Check the hotel, room and vehicle selections.")}
                />
              ) : null}
              {isUnpriced ? null : totalPriceMutation.data?.warnings?.map((warning) => <Alert key={warning} type="warning" showIcon message={warning} />)}
              <QuotePricingCard
                organisationName={organisationName}
                currency={totalPriceMutation.data?.totalPrice.currency || itineraryCurrency}
                supplierTotal={isUnpriced ? null : totalPriceMutation.data?.totalPrice.amount ?? null}
                calculating={totalPriceMutation.isPending}
                adultCount={Number(values?.adults || 0)}
                childCosts={totalPriceMutation.data?.childCosts}
                supplierPassengers={totalPriceMutation.data?.supplierPassengers}
                perPerson={totalPriceMutation.data?.perPerson}
                childPrice={
                  (values?.cwb || 0) > 0
                    ? totalPriceMutation.data?.perPerson?.cwb
                    : (values?.cnb || 0) > 0
                      ? totalPriceMutation.data?.perPerson?.cnb
                      : undefined
                }
                markupDraft={markupDraft}
                onEditMarkup={() => setMarkupModalOpen(true)}
              />
              <QuoteActionsCard
                organisationName={organisationName}
                canSubmit={hasItems && !isUnpriced}
                saving={saveDraftMutation.isPending && !confirmationValues}
                confirming={submitMutation.isPending}
                onSave={() => {
                  setActionIntent("save");
                  form.submit();
                }}
                onConfirm={() => {
                  setActionIntent("submit");
                  form.submit();
                }}
                savedQuote={
                  editingItineraryId
                    ? {
                        hasUnsavedChanges: isDirty || markupDirty,
                        downloading,
                        duplicating: duplicateMutation.isPending,
                        onDownload: () => setDownloadModalOpen(true),
                        onDuplicate: () => void duplicateSavedQuote(),
                      }
                    : undefined
                }
              />
              <QuoteMarkupModal
                open={markupModalOpen}
                initial={markupDraft}
                currency={totalPriceMutation.data?.totalPrice.currency || itineraryCurrency}
                supplierTotal={totalPriceMutation.data?.totalPrice.amount ?? null}
                organisationName={organisationName}
                supplierTax={totalPriceMutation.data?.supplierPassengers?.reduce((sum, row) => sum + row.supplierTax * row.quantity, 0)}
                canRemove={Boolean(draftToMarkup(markupDraft))}
                saveLabel="Apply"
                onCancel={() => setMarkupModalOpen(false)}
                onSave={(next) => {
                  setMarkupDraft(next);
                  setMarkupDirty(true);
                  setMarkupModalOpen(false);
                }}
                onRemove={() => {
                  setMarkupDraft(emptyMarkupDraft());
                  setMarkupDirty(true);
                  setMarkupModalOpen(false);
                }}
              />
            </aside>
          </div>
          <Modal
            open={editingService?.type === "hotel"}
            title="Hotel details"
            width={820}
            onCancel={() => setEditingService(null)}
            footer={
              <Button type="primary" onClick={() => setEditingService(null)}>
                Done
              </Button>
            }
          >
            {editingService?.type === "hotel" ? (
              <div className="space-y-5">
                <div className="rounded-lg border border-gray-200 bg-[#FBFBFB] p-4">
                  <InventoryProductHeader item={editingInventory} fallback="Hotel" />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <Form.Item
                    className="mb-0! md:col-span-2"
                    label="Stay dates"
                    name={["hotels", editingService.index, "stayDates"]}
                    rules={[{ required: true, message: "Choose stay dates" }]}
                  >
                    <DatePicker.RangePicker
                      className="w-full"
                      format="DD MMM YYYY"
                      minDate={dayjs(startDate || initialValues.startDate)}
                      maxDate={dayjs(startDate || initialValues.startDate).add(Number(nights), "day")}
                    />
                  </Form.Item>
                  <Form.Item
                    className="mb-0!"
                    label="Room type"
                    name={["hotels", editingService.index, "roomType"]}
                    rules={[{ required: true, message: "Select a room type" }]}
                  >
                    <Select
                      options={(editingInventory?.roomOptions || []).map((room) => ({
                        value: room.roomType,
                        label: room.roomType,
                      }))}
                      onChange={(roomType) => {
                        const room = editingInventory?.roomOptions?.find((option) => option.roomType === roomType);
                        form.setFieldValue(["hotels", editingService.index, "mealPlan"], room?.mealPlans?.[0]);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    className="mb-0!"
                    label="Meal plan"
                    name={["hotels", editingService.index, "mealPlan"]}
                    rules={[{ required: true, message: "Select a meal plan" }]}
                  >
                    <Select
                      options={(
                        editingInventory?.roomOptions?.find(
                          (room) => room.roomType === form.getFieldValue(["hotels", editingService.index, "roomType"]),
                        )?.mealPlans || []
                      ).map((plan) => ({ value: plan, label: plan }))}
                    />
                  </Form.Item>
                  <Form.Item
                    className="mb-0!"
                    label="Rooms"
                    name={["hotels", editingService.index, "numberOfRooms"]}
                    rules={[{ required: true }]}
                  >
                    <InputNumber className="w-full" min={1} precision={0} />
                  </Form.Item>
                  <div>
                    <p className="mb-2 text-sm font-medium text-[#131B2A]">Room capacity</p>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-600">
                      {editingInventory?.roomOptions?.find(
                        (room) => room.roomType === form.getFieldValue(["hotels", editingService.index, "roomType"]),
                      )?.paxPerRoom || 1}{" "}
                      pax per room
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <p className="mb-3 text-xs text-gray-500">
                    Occupancy is automatically allocated from the traveller mix and room capacity.
                  </p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Form.Item
                      className="mb-0!"
                      label="Adults + extra bed"
                      name={["hotels", editingService.index, "aweb"]}
                    >
                      <InputNumber disabled className="w-full" />
                    </Form.Item>
                    <Form.Item className="mb-0!" label="Child + bed" name={["hotels", editingService.index, "cweb"]}>
                      <InputNumber disabled className="w-full" />
                    </Form.Item>
                    <Form.Item className="mb-0!" label="Child no bed" name={["hotels", editingService.index, "cnb"]}>
                      <InputNumber disabled className="w-full" />
                    </Form.Item>
                    <Form.Item className="mb-0!" label="Infants" name={["hotels", editingService.index, "infants"]}>
                      <InputNumber disabled className="w-full" />
                    </Form.Item>
                  </div>
                </div>
              </div>
            ) : null}
          </Modal>

          <Modal
            open={editingService?.type === "activity"}
            title="Activity details"
            width={760}
            onCancel={() => setEditingService(null)}
            footer={
              <Button type="primary" onClick={() => setEditingService(null)}>
                Done
              </Button>
            }
          >
            {editingService?.type === "activity" ? (
              <div className="space-y-5">
                <div className="rounded-lg border border-gray-200 bg-[#FBFBFB] p-4">
                  <InventoryProductHeader item={editingInventory} fallback="Activity" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Form.Item
                    className="mb-0!"
                    label="Quantity"
                    name={["days", editingService.dayIndex, "activities", editingService.index, "quantity"]}
                    rules={[{ required: true }]}
                  >
                    <InputNumber className="w-full" min={1} precision={0} />
                  </Form.Item>
                  {activitySupportsVehicle(editingInventory) ? (
                    <Form.Item
                      className="mb-0!"
                      label="Vehicle"
                      name={[
                        "days",
                        editingService.dayIndex,
                        "activities",
                        editingService.index,
                        "selectedVehicleType",
                      ]}
                      rules={[{ required: true, message: "Select a vehicle" }]}
                    >
                      <Select
                        placeholder="Select vehicle"
                        options={(editingInventory?.vehicleOptions || []).map((vehicle) => ({
                          value: vehicleOptionValue(vehicle),
                          label: vehicle.pax ? `${vehicle.vehicleType} · ${vehicle.pax} pax` : vehicle.vehicleType,
                        }))}
                      />
                    </Form.Item>
                  ) : null}
                </div>
              </div>
            ) : null}
          </Modal>

          <Modal
            open={editingService?.type === "transport"}
            title="Transport details"
            width={820}
            onCancel={() => setEditingService(null)}
            footer={
              <Button type="primary" onClick={() => setEditingService(null)}>
                Done
              </Button>
            }
          >
            {editingService?.type === "transport" ? (
              <div className="space-y-5">
                <div className="rounded-lg border border-gray-200 bg-[#FBFBFB] p-4">
                  <InventoryProductHeader item={editingInventory} fallback="Transport" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Form.Item
                    className="mb-0!"
                    label="Start time"
                    name={["days", editingService.dayIndex, "transports", editingService.index, "startTime"]}
                    getValueProps={(value) => ({ value: value ? dayjs(value, "HH:mm") : null })}
                    normalize={(value) => (value ? value.format("HH:mm") : null)}
                  >
                    <TimePicker className="w-full" format="HH:mm" prefix={<Clock3Icon size={13} />} />
                  </Form.Item>
                  {transportHasEditableDuration(editingInventory) ? (
                    <Form.Item
                      className="mb-0!"
                      label={editingInventory?.serviceType === "cab" ? "Hours" : "Duration"}
                      name={["days", editingService.dayIndex, "transports", editingService.index, "durationHours"]}
                      rules={
                        editingInventory?.rateBasis === "hourly"
                          ? [{ required: true, message: "Enter duration" }]
                          : undefined
                      }
                    >
                      <InputNumber className="w-full" min={0.5} step={0.5} addonAfter="hrs" />
                    </Form.Item>
                  ) : (
                    <div>
                      <p className="mb-2 text-sm font-medium text-[#131B2A]">Duration</p>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-600">
                        {editingInventory?.duration ? `${editingInventory.duration} hrs` : "Fixed service"}
                      </div>
                    </div>
                  )}
                  <Form.Item
                    className="mb-0!"
                    label="Quantity"
                    name={["days", editingService.dayIndex, "transports", editingService.index, "quantity"]}
                  >
                    <InputNumber className="w-full" min={1} precision={0} />
                  </Form.Item>
                  <Form.Item
                    className="mb-0!"
                    label="Vehicle"
                    name={["days", editingService.dayIndex, "transports", editingService.index, "selectedVehicleType"]}
                  >
                    <Select
                      allowClear
                      placeholder="Select vehicle"
                      options={(editingInventory?.vehicleOptions || []).map((vehicle) => ({
                        value: vehicleOptionValue(vehicle),
                        label: vehicle.pax ? `${vehicle.vehicleType} · ${vehicle.pax} pax` : vehicle.vehicleType,
                      }))}
                    />
                  </Form.Item>
                </div>
              </div>
            ) : null}
          </Modal>

          <Modal
            open={editingService?.type === "fullDayTransport"}
            title="Full Day transport details"
            width={820}
            onCancel={() => setEditingService(null)}
            footer={
              <Button type="primary" onClick={() => setEditingService(null)}>
                Done
              </Button>
            }
          >
            {editingService?.type === "fullDayTransport" ? (
              <div className="space-y-5">
                <div className="rounded-lg border border-gray-200 bg-[#FBFBFB] p-4">
                  <InventoryProductHeader item={editingInventory} fallback="Full Day transport" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Form.Item
                    className="mb-0! sm:col-span-2"
                    label="Service dates"
                    name={["dailyTransports", editingService.index, "serviceDates"]}
                    rules={[{ required: true, message: "Select service dates" }]}
                  >
                    <DatePicker.RangePicker
                      className="w-full"
                      format="DD MMM YYYY"
                      minDate={dayjs(startDate || initialValues.startDate)}
                      maxDate={dayjs(startDate || initialValues.startDate).add(Number(nights), "day")}
                      onChange={(dates) => {
                        form.setFieldValue(
                          ["dailyTransports", editingService.index, "startDate"],
                          dates?.[0]?.format("YYYY-MM-DD") || null,
                        );
                        form.setFieldValue(
                          ["dailyTransports", editingService.index, "endDate"],
                          dates?.[1]?.format("YYYY-MM-DD") || null,
                        );
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    className="mb-0!"
                    label="Quantity"
                    name={["dailyTransports", editingService.index, "quantity"]}
                    rules={[{ required: true, message: "Enter quantity" }]}
                  >
                    <InputNumber className="w-full" min={1} precision={0} />
                  </Form.Item>
                  <Form.Item
                    className="mb-0!"
                    label="Vehicle"
                    name={["dailyTransports", editingService.index, "selectedVehicleType"]}
                    rules={[
                      { required: Boolean(editingInventory?.vehicleOptions?.length), message: "Select a vehicle" },
                    ]}
                  >
                    <Select
                      allowClear
                      placeholder="Select vehicle"
                      options={(editingInventory?.vehicleOptions || []).map((vehicle) => ({
                        value: vehicleOptionValue(vehicle),
                        label: vehicle.pax ? `${vehicle.vehicleType} · ${vehicle.pax} pax` : vehicle.vehicleType,
                      }))}
                    />
                  </Form.Item>
                </div>
                {editingFullDayTransport?.startDate && editingFullDayTransport?.endDate ? (
                  <p className="text-xs text-gray-500">
                    {dayjs(editingFullDayTransport.endDate).diff(dayjs(editingFullDayTransport.startDate), "day") + 1}{" "}
                    inclusive service day(s)
                  </p>
                ) : null}
              </div>
            ) : null}
          </Modal>
        </Form>
        </div>
        <PortalInventoryBrowserModal
          open={Boolean(browserTarget)}
          type={browserTarget?.type || "hotel"}
          destinations={destinations}
          onClose={() => setBrowserTarget(null)}
          onSelect={addBrowsedInventory}
          onSelectWithLinkedActivities={addBrowsedTransportsWithLinkedActivities}
          rateBasisFilter={
            browserTarget?.type === "transport" ? (browserTarget.daily ? "daily" : "non_daily") : undefined
          }
          fullDayMode={Boolean(browserTarget?.type === "transport" && browserTarget.daily)}
          mode={browserTarget?.replaceIndex !== undefined ? "replace" : "add"}
        />
        <Modal
          open={isRoutePromptOpen}
          onCancel={() => {
            setRouteStops([]);
            setIsRoutePromptOpen(false);
            if (openTemplateAfterRouteFlow) {
              setOpenTemplateAfterRouteFlow(false);
              setIsTemplateModalOpen(true);
            }
          }}
          title={null}
          footer={null}
          width="calc(100vw - 24px)"
          style={{ maxWidth: 620, top: 24 }}
        >
          <div className="pt-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-(--brand-primary) text-[var(--brand-primary-foreground)]">
              <RouteIcon className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-tight text-[#131B2A]">Plan this route by cities?</h2>
            <p className="mt-2 max-w-[58ch] text-sm leading-6 text-gray-500">
              The trip includes {destinations.join(", ")}. Add city stops to assign dates and make hotel, activity, and
              transport searches more relevant.
            </p>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => {
                  setIsRoutePromptOpen(false);
                  openRoutePlanner();
                }}
                className="group rounded-xl border-2 border-(--brand-secondary) bg-(--brand-secondary) p-4 text-left text-white transition-transform duration-150 hover:-translate-y-0.5"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-(--brand-primary) text-[var(--brand-primary-foreground)]">
                  <RouteIcon className="h-4 w-4" />
                </span>
                <span className="mt-4 block text-sm font-semibold">Add city stops</span>
                <span className="mt-1 block text-xs leading-5 text-gray-400">
                  Choose cities and allocate nights; dates are calculated automatically.
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setRouteStops([]);
                  setIsRoutePromptOpen(false);
                  if (openTemplateAfterRouteFlow) {
                    setOpenTemplateAfterRouteFlow(false);
                    setIsTemplateModalOpen(true);
                  }
                }}
                className="group rounded-xl border border-gray-200 bg-[#FBFBFB] p-4 text-left transition-colors duration-150 hover:border-(--brand-primary)"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500">
                  <MapPinIcon className="h-4 w-4" />
                </span>
                <span className="mt-4 block text-sm font-semibold text-[#131B2A]">Use countries only</span>
                <span className="mt-1 block text-xs leading-5 text-gray-500">
                  Continue with the trip destinations and add city details later.
                </span>
              </button>
            </div>
          </div>
        </Modal>
        <PortalRoutePlannerPage
          open={isRoutePlannerOpen}
          countries={destinations}
          startDate={startDate || dayjs()}
          totalNights={Number(nights)}
          value={routePlannerValue}
          onCancel={finishRouteFlow}
          onUseCountriesOnly={() => {
            setRouteStops([]);
            finishRouteFlow();
          }}
          onSave={(nextRouteStops, allocatedNights) => {
            form.setFieldValue("nights", allocatedNights);
            setRouteStops(nextRouteStops);
            finishRouteFlow();
          }}
        />
        <PortalTemplatePickerPage
          open={isTemplateModalOpen}
          onClose={() => setIsTemplateModalOpen(false)}
          destinations={destinations}
          nights={Number(nights)}
          routeStops={routeStops}
          onAdjustRoute={() => {
            setIsTemplateModalOpen(false);
            setOpenTemplateAfterRouteFlow(true);
            openRoutePlanner();
          }}
          onApply={(plan) => {
            applyTemplatePlanToBuilder(plan, false, false);
          }}
        />
        <Modal
          title="Confirm this quote?"
          open={Boolean(confirmationValues)}
          closable={!submitMutation.isPending && !saveDraftMutation.isPending}
          maskClosable={!submitMutation.isPending && !saveDraftMutation.isPending}
          onCancel={() => {
            if (submitMutation.isPending || saveDraftMutation.isPending) return;
            setConfirmationValues(null);
            setSubmissionError(null);
          }}
          footer={[
            <Button
              key="edit"
              disabled={submitMutation.isPending || saveDraftMutation.isPending}
              onClick={() => {
                setConfirmationValues(null);
                setSubmissionError(null);
              }}
            >
              Continue editing
            </Button>,
            <Button
              key="submit"
              type="primary"
              icon={<SendIcon size={15} />}
              loading={submitMutation.isPending || saveDraftMutation.isPending}
              disabled={submitMutation.isPending || saveDraftMutation.isPending}
              onClick={submit}
              className="!border-(--brand-secondary) !bg-(--brand-secondary)"
            >
              Confirm Quote
            </Button>,
          ]}
        >
          <p className="text-sm leading-6 text-gray-600">
            Confirming sends this quote to {organisationName}. You will not be able to edit any quote in this trip
            unless {organisationName} returns it to you.
          </p>
          {submissionError ? <Alert className="mt-4" type="error" showIcon message={submissionError} /> : null}
          {confirmationValues ? (
            <div className="mt-4 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-[#FBFBFB] px-4 text-sm">
              <div className="flex justify-between gap-4 py-2.5">
                <span className="text-gray-500">Destinations</span>
                <span className="text-right font-medium">{confirmationValues.destinations.join(", ")}</span>
              </div>
              <div className="flex justify-between gap-4 py-2.5">
                <span className="text-gray-500">Travel dates</span>
                <span className="text-right font-medium">
                  {dayjs(confirmationValues.startDate).format("DD MMM YYYY")} · {confirmationValues.nights} nights
                </span>
              </div>
              <div className="flex justify-between gap-4 py-2.5">
                <span className="text-gray-500">Selling currency</span>
                <span className="font-medium">{confirmationValues.conversionCurrency || itineraryCurrency}</span>
              </div>
              <div className="flex justify-between gap-4 py-2.5">
                <span className="text-gray-500">Travellers</span>
                <span className="text-right font-medium">
                  {confirmationValues.adults} adults, {confirmationValues.cwb + confirmationValues.cnb} children,{" "}
                  {confirmationValues.infant} infants
                </span>
              </div>
              <div className="flex justify-between gap-4 py-2.5">
                <span className="text-gray-500">Hotels</span>
                <span className="font-medium">{confirmationValues.hotels.length}</span>
              </div>
              <div className="flex justify-between gap-4 py-2.5">
                <span className="text-gray-500">Transports</span>
                <span className="font-medium">
                  {confirmationValues.days.reduce((sum, day) => sum + day.transports.length, 0)}
                </span>
              </div>
              <div className="flex justify-between gap-4 py-2.5">
                <span className="text-gray-500">Activities</span>
                <span className="font-medium">
                  {confirmationValues.days.reduce((sum, day) => sum + day.activities.length, 0)}
                </span>
              </div>
              {totalPriceMutation.data ? (
                <div className="flex justify-between gap-4 py-2.5">
                  <span className="text-gray-500">Total price</span>
                  <span className="font-semibold">
                    {formatMoney(
                      totalPriceMutation.data.totalPrice.amount,
                      totalPriceMutation.data.totalPrice.currency,
                    )}
                  </span>
                </div>
              ) : null}
              {confirmationValues.comments?.trim() ? (
                <div className="py-2.5">
                  <span className="text-gray-500">Request notes</span>
                  <p className="mt-1 whitespace-pre-wrap text-gray-700">{confirmationValues.comments.trim()}</p>
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal>
      </div>
      <DownloadItineraryModal
        open={downloadModalOpen}
        onCancel={() => setDownloadModalOpen(false)}
        onDownload={downloadSavedQuote}
        agentMode
      />
    </PortalPageShell>
  );
};

export default PortalItineraryBuilderPage;
